import TaxTreatmentFields from './TaxTreatmentFields';
import React, { useState, useEffect, useCallback, useRef } from "react";
/* global axios */
import api, { calculateRentalPeriodToApi } from '../services/apiService';
import { useParams, useNavigate } from "react-router-dom";
import Select from 'react-select';
import debounce from 'lodash.debounce';
import { Helmet } from 'react-helmet';  // Import von React Helmet
import { v4 as uuidv4 } from 'uuid';



// Funktion zur Formatierung des Datums in "TT.MM.JJJJ"
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

// Funktion zur Formatierung von Datum+Uhrzeit in "TT.MM.JJJJ HH:MM"
const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day     = String(date.getDate()).padStart(2, '0');
  const month   = String(date.getMonth() + 1).padStart(2, '0');
  const year    = date.getFullYear();
  const hours   = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

// ✅ Zentral: berechnet den Zeilen-Total zuverlässig (wie OfferForm)
const calcRowTotal = (row, allRows) => {
  if (!row) return 0;

  const qty = Number(row.quantity || 0);
  const ppu = Number(row.pricePerUnit || 0);
  const type = row.item?.itemType;

  // insurance (%) -> basiert auf Hauptartikel (mainItemId)
  if (type === "insurance") {
    const main = allRows.find(r => r.id === row.mainItemId);
    if (!main) return 0;

    const mainQty = Number(main.quantity || 0);
    const mainPPU = Number(main.pricePerUnit || 0);
    const perc = qty;

    const insurancePPU = (mainPPU * perc) / 100;
    return insurancePPU * mainQty;
  }

  // insuranceFixed -> normal qty * ppu
  if (type === "insuranceFixed") {
    return qty * ppu;
  }

  // normal
  return qty * ppu;
};


const OrderForm = ({ mode, onSave }) => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState({
    customerId: "",
    contactId: "",
    lwOperatorId: "",  // Ansprechpartner vor Ort hinzufügen
    deliveryTime: "",
    operationLocation: "",
    notes: "",
    internalNotes: "",
    transportNotes: "",
    date: "",
    validUntil: "",
    status: "pending", // Standardstatus für Aufträge
    referenceNumber: "", 
    smartRental: false,            // Neu: SmartRental Checkbox
    obligationInstruction: false,
    includeLaddersInEmail: false, 
  });
  const [orderNoteTextBlocks, setOrderNoteTextBlocks] = useState([]);
  const [selectedOrderNoteTextBlockId, setSelectedOrderNoteTextBlockId] = useState('');
  const [orderNoteTextBlocksLoading, setOrderNoteTextBlocksLoading] = useState(false);

  // Temporäre Zustände für die Checkboxen
  const [tempIncludeSaturday, setTempIncludeSaturday] = useState(false);
  const [tempIncludeSunday, setTempIncludeSunday] = useState(false);
  const [tempIncludeHolidays, setTempIncludeHolidays] = useState(false);

  // Zustände für Auftragspositionen und verfügbare Artikel
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [orderItems, setOrderItems] = useState([]);  // State für Auftragspositionen
  const [items, setItems] = useState([]);  // State für alle verfügbaren Artikel
  const [editingRowId, setEditingRowId] = useState(null); // Verfolgt die aktuell bearbeitete Zeile
  const [units, setUnits] = useState([]); // State für Einheiten
  const [selectedUnitId, setSelectedUnitId] = useState(null); // Zustand für die zuletzt verwendete Einheit
  const isEditing = editingRowId !== null;
  const [tempRentalPeriodFrom, setTempRentalPeriodFrom] = useState("");
  const [tempRentalPeriodTo, setTempRentalPeriodTo] = useState("");
  const [tempRentalTimeFrom, setTempRentalTimeFrom] = useState("");
  const [tempRentalTimeTo, setTempRentalTimeTo] = useState("");
  const [originalOrderItems, setOriginalOrderItems] = useState([]);
 
  // Dialog anzeigen oder verbergen
  const [showWorkshopOrderDialog, setShowWorkshopOrderDialog] = useState(false);
  // Vom Dialog ausgewählte Rental-Artikel (Option aus OrderItems)
  const [selectedRentalArticle, setSelectedRentalArticle] = useState(null);
  // Vom Dialog ausgewählter WorkshopOrderType
  const [selectedWorkshopOrderType, setSelectedWorkshopOrderType] = useState(null);
  const [workshopOrders, setWorkshopOrders] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [lwPartnerId, setLwPartnerId] = useState(null);
  const [companySetup, setCompanySetup] = useState(null);


// --- OperationLocation Suggest (analog WorkshopOrderForm) ---
const [opLocInput, setOpLocInput] = useState('');
const [opLocSuggest, setOpLocSuggest] = useState([]);
const [opLocOpen, setOpLocOpen] = useState(false);
const [opLocLoading, setOpLocLoading] = useState(false);
const [opLocIndex, setOpLocIndex] = useState(-1);
const opLocWrapRef = useRef(null);

const rememberOpLoc = useCallback(async (label) => {
  try {
    const trimmed = String(label || '').trim();
    if (!trimmed) return;
    await api.post('/operation-locations/remember', { label: trimmed });
  } catch (e) {
    console.warn('[rememberOpLoc] failed:', e?.response?.data || e?.message || e);
  }
}, []);

const fetchOpLocSuggest = useCallback(
  debounce(async (q) => {
    try {
      setOpLocLoading(true);
      const { data } = await api.get('/operation-locations/suggest', { params: { q, limit: 12 } });
      setOpLocSuggest(Array.isArray(data) ? data : []);
      setOpLocOpen(true);
    } catch (e) {
      console.error('operationLocation suggest failed', e);
      setOpLocSuggest([]);
    } finally {
      setOpLocLoading(false);
    }
  }, 250),
  []
);

useEffect(() => {
  return () => fetchOpLocSuggest.cancel?.();
}, [fetchOpLocSuggest]);

useEffect(() => {
  setOpLocInput(order?.operationLocation || '');
}, [order?.operationLocation]);

const onOpLocChange = (e) => {
  const v = e.target.value;
  setOpLocInput(v);
  setOrder(prev => ({ ...prev, operationLocation: v }));
  fetchOpLocSuggest(v.trim());
};

const onOpLocFocus = () => {
  fetchOpLocSuggest('');
};

const onOpLocBlur = async () => {
  const v = (opLocInput || '').trim();
  if (!v) return;
  await rememberOpLoc(v);
};

const selectOpLoc = async (label) => {
  setOrder(prev => ({ ...prev, operationLocation: label }));
  setOpLocInput(label);
  setOpLocOpen(false);
  setOpLocIndex(-1);
  await rememberOpLoc(label);
};

useEffect(() => {
  const onDocClick = (e) => {
    if (!opLocWrapRef.current) return;
    if (!opLocWrapRef.current.contains(e.target)) {
      setOpLocOpen(false);
      setOpLocIndex(-1);
    }
  };
  document.addEventListener('mousedown', onDocClick);
  return () => document.removeEventListener('mousedown', onDocClick);
}, []);

const onOpLocKeyDown = async (e) => {
  if (opLocOpen && opLocSuggest.length) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpLocIndex((i) => (i + 1) % opLocSuggest.length);
      return;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpLocIndex((i) => (i - 1 + opLocSuggest.length) % opLocSuggest.length);
      return;
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = opLocSuggest[opLocIndex] || opLocSuggest[0];
      if (pick) {
        await selectOpLoc(pick.label);
        return;
      }
    } else if (e.key === 'Escape') {
      setOpLocOpen(false);
      setOpLocIndex(-1);
      return;
    }
  }
  if (e.key === 'Enter') {
    const v = (opLocInput || '').trim();
    if (v) {
      setOrder(prev => ({ ...prev, operationLocation: v }));
      await rememberOpLoc(v);
      setOpLocOpen(false);
      setOpLocIndex(-1);
    }
  }
};
// --- /OperationLocation Suggest ---


  // Feiertage einmal beim Mount laden
useEffect(() => {
  api.get('/holidays')
    .then(res => setHolidays(res.data))
    .catch(console.error);
}, []);

useEffect(() => {
  if (mode !== 'edit') return undefined;

  let isMounted = true;
  setOrderNoteTextBlocksLoading(true);

  api.get('/text-blocks', {
    params: { usageArea: 'orderNotes', activeOnly: true },
  })
    .then((response) => {
      if (isMounted) {
        setOrderNoteTextBlocks(Array.isArray(response.data) ? response.data : []);
      }
    })
    .catch((error) => {
      console.error('Fehler beim Laden der Textbausteine für Auftragsnotizen:', error);
      if (isMounted) setOrderNoteTextBlocks([]);
    })
    .finally(() => {
      if (isMounted) setOrderNoteTextBlocksLoading(false);
    });

  return () => {
    isMounted = false;
  };
}, [mode]);

// lwLicensePartnerId aus CompanySetup laden (erstes Setup genügt)
useEffect(() => {
  (async () => {
    try {
      const res = await api.get('/companysetups'); // gibt Array zurück
      const first = Array.isArray(res.data) && res.data.length ? res.data[0] : null;
      setLwPartnerId(first?.lwLicensePartnerId ? String(first.lwLicensePartnerId).trim() : null);
      setCompanySetup(first || null); // <-- NEU: komplettes Setup merken
    } catch (e) {
      console.error('CompanySetup laden fehlgeschlagen:', e);
    }
  })();
}, []);


// Ganz oben bei den anderen Hilfsfunktionen:
const safeFilterOption = (option, inputValue) => {
  const label = (option?.label ?? '').toString();
  const q = (inputValue ?? '').toString();
  return label.toLowerCase().includes(q.toLowerCase());
};

const EMPTY_CONTACT = {
  contactFirstName: '',
  contactLastName: '',
  contactPhone: '',
  contactMobile: '',
  contactEmail: '',
};

const formatItemOptionLabel = (item) => {
  const description = String(item?.description || '').trim();
  return `${item.name}${description ? ` - ${description}` : ''} (${item.itemType})`;
};


  // Speichert nur eine einzelne Zeile
const saveEditedRowOrder = async (rowId) => {
  try {
    // 1) Finde den Eintrag anhand seiner echten DB-ID
    const item = orderItems.find(i => i.id === rowId);
    if (!item) return;

    // Prüfen, ob es sich um einen Mietartikel handelt
    const isRental = item.item?.itemType === "rental";

    // Nur beim Miet-Typ muss rentalPeriodFrom gesetzt sein
    if (isRental && !item.rentalPeriodFrom) {
      return alert("Bitte das Datum ‚Von‘ ausfüllen, bevor Du speicherst.");
    }

    // 2) Baue Payload aus diesem einzelnen Eintrag
    const payload = {
      quantity:           item.quantity,
      unitId:             item.unitId,
      rentalPeriodFrom:   item.rentalPeriodFrom,
      // rentalPeriodTo wird serverseitig neu berechnet, muss nicht gesendet
      rentalTimeFrom:     item.rentalTimeFrom,
      rentalTimeTo:       item.rentalTimeTo,
      includeSaturday:    item.includeSaturday,
      includeSunday:      item.includeSunday,
      includeHolidays:    item.includeHolidays,
      pricePerUnit:       item.pricePerUnit,
      manualPriceActive:  item.manualPriceActive,
      description: item.description,
    };

    // 3) PUT-Request mit der echten DB-id
    const { data: updated } = await api.put(
      `/orders/${order.id}/orderitems/${rowId}`,
      payload
    );

    // 4) NEU: updatedBy auf null setzen
    await api.post(`/orders/${order.id}/reset-updatedby`);

    // 5) State: Ersetze nur genau dieses eine Objekt durch die vollständige Server-Antwort
    setOrderItems(prev =>
  prev
    .map(i => {
      if (i.id !== updated.id) return i;

      const { total, ...rest } = updated; // ✅ total raus
      return {
        ...rest,
        tempId: i.tempId ?? updated.id,              // falls du tempId brauchst
        manualPriceActive: !!rest.manualPriceActive, // robust
      };
    })
    .sort((a, b) => (a.designPosition ?? a.position) - (b.designPosition ?? b.position))
);


    // 6) Bearbeitungsmodus beenden
    setEditingRowId(null);

  } catch (error) {
    console.error(`Fehler beim Speichern der Position ${rowId}:`, error);
    alert(`Fehler: Die Änderung der Position ${rowId} konnte nicht gespeichert werden.`);
  }
};
  

  // Hilfsfunktion zum Parsen von Zeitstrings "HH:MM" in Millisekunden seit Mitternacht
  const parseTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return (hours * 60 + minutes) * 60 * 1000; // Millisekunden
  };

  const handleCreateWorkshopOrder = async () => {
    try {
      // Sende einen POST-Request an den API-Endpunkt zum Generieren eines Werkstattauftrags
      const response = await api.post(`/workshoporders/fromOrder/${orderId}`);
      alert('Werkstattauftrag wurde erfolgreich erstellt!');
      // Navigiere zur Detailansicht oder Bearbeitungsseite des neu erstellten Werkstattauftrags
      navigate(`/workshoporders/${response.data.id}/edit`);
    } catch (error) {
      console.error('Fehler beim Erstellen des Werkstattauftrags:', error);
      alert('Fehler beim Erstellen des Werkstattauftrags.');
    }
  };

  // Filtere alle OrderItems, die als Rental-Artikel gelten:
const rentalOptions = orderItems
.filter(item => item.item?.itemType === 'rental')
.map(item => ({
  value: item.itemId, // oder alternativ item.id, je nach gewünschtem Schlüssel
  label: item.item?.name,
}));

// WorkshopOrderType-Optionen (entsprechend deinem Enum)
const workshopOrderTypeOptions = [
  { value: 'inspection', label: 'Inspektion' },
  { value: 'repair', label: 'Reparatur' },
  { value: 'refuel', label: 'Tanken' },
  { value: 'cleaning', label: 'Reinigung' },
  { value: 'precheck', label: 'Vorkontrolle' },
  { value: 'postcheck', label: 'Nachkontrolle' },
];

const handleConfirmWorkshopOrderCreation = async () => {
  if (!selectedRentalArticle || !selectedWorkshopOrderType) {
    alert("Bitte wählen Sie sowohl einen Rental-Artikel als auch einen Auftragstyp aus.");
    return;
  }
  try {
    // Sende einen POST-Request an deinen API-Endpunkt.
    const response = await api.post(
      `/workshoporders/fromOrder/${orderId}`,
      {
        workshopOrderType: selectedWorkshopOrderType.value,
        rentalArticleId: selectedRentalArticle.value,
      }
    );
    alert("Werkstattauftrag wurde erfolgreich erstellt!");
    setShowWorkshopOrderDialog(false);
    navigate(`/workshoporders/${response.data.id}/edit`);
  } catch (error) {
    console.error("Fehler beim Erstellen des Werkstattauftrags:", error);
    alert("Fehler beim Erstellen des Werkstattauftrags.");
  }
};

 /**
 * Prüft, ob ein JS-Date-Objekt auf einen Feiertag fällt,
 * basierend auf dem im State geladenen holidays-Array.
 */
const isHolidayDate = (date) => {
  const m = date.getMonth();    // JS: 0 = Januar … 11 = Dezember
  const d = date.getDate();     // 1–31
  return holidays.some(h =>
    Number(h.month) === m &&
    Number(h.day)   === d
  );
};

const updateRentalPeriodTo = useCallback(async (itemsToUpdate) => {
  // nur übergebene Items berechnen – NICHT automatisch orderItems
  const base = Array.isArray(itemsToUpdate) ? itemsToUpdate : [];
  if (!base.length) return;

  const computed = await Promise.all(
    base.map(async (item) => {
      if (
        item.item?.itemType === "rental" &&
        item.rentalPeriodFrom &&
        item.quantity &&
        item.unitId
      ) {
        try {
          const { data } = await calculateRentalPeriodToApi({
            rentalPeriodFrom: item.rentalPeriodFrom,
            quantity: item.quantity,
            unitId: item.unitId,
            includeSaturday: item.includeSaturday,
            includeSunday: item.includeSunday,
            includeHolidays: item.includeHolidays,
          });

          if (data?.rentalPeriodTo && data.rentalPeriodTo !== item.rentalPeriodTo) {
            return { id: item.id, rentalPeriodTo: data.rentalPeriodTo };
          }
        } catch (err) {
          console.warn("Konnte rentalPeriodTo nicht berechnen:", err);
        }
      }
      return null;
    })
  );

  const patches = computed.filter(Boolean);
  if (!patches.length) return;

  const patchMap = new Map(patches.map((p) => [p.id, p.rentalPeriodTo]));

  setOrderItems((prev) =>
    prev.map((it) => {
      const newTo = patchMap.get(it.id);
      return newTo ? { ...it, rentalPeriodTo: newTo } : it;
    })
  );
}, []); // ✅ leer lassen (calculateRentalPeriodToApi ist importiert/stabil)






  // Funktion zur Aktualisierung der Einheit im Backend
  const updateOrderItemUnit = async (orderId, orderItemId, newUnitId, newQuantity) => {
    try {
      const payload = {
  unitId: newUnitId,
  quantity: newQuantity,
};


      const response = await api.put(
        `/orders/${orderId}/orderitems/${orderItemId}`, 
        payload
      );
      console.log('Einheit und Menge erfolgreich aktualisiert:', response.data);
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Einheit:', error);
    }
  };

  const getMatchingPricing = (item, quantity, newUnitId) => {
    // Überprüfen, ob die Preisstaffelung existiert für die neue Einheit (newUnitId)
    const pricing = item.item.machineType?.typePricings.find((pricing) => {
      return quantity >= pricing.minQuantity && quantity <= pricing.maxQuantity && pricing.unitId === newUnitId;
    });

    if (pricing) {
      const pricePerUnit = pricing.pricePerUnit;
      const total = pricePerUnit * quantity;
      return { pricePerUnit, total };
    } else {
      // Wenn keine Preisstaffelung gefunden wird, setze Standardwerte
      return { pricePerUnit: 0, total: 0 };
    }
  };

  // Auftragspositionen entfernen
  const removeOrderItem = async (orderItemId) => {
    console.log("Remove-Handler aufgerufen mit ID:", orderItemId);
    try {
      console.log(`Entferne Auftragsposition mit ID: ${orderItemId} für Auftrag ID: ${order.id}`);

      // API-Aufruf, um die Auftragsposition zu entfernen
      const response = await api.delete(`/orders/${order.id}/orderitems/${orderItemId}`);
      console.log('API-Antwort beim Entfernen:', response.data, 'Status:', response.status);

      // Überprüfen, ob die Antwort erfolgreich war
      if (response.status === 200 || response.status === 204) {
        // Entferne das OrderItem aus dem lokalen State
        setOrderItems((prevItems) => {
          const updatedItems = prevItems.filter((item) => item.id !== orderItemId);
          console.log("Aktualisierte Auftragspositionen nach dem Entfernen:", updatedItems); // Log hinzufügen
          return updatedItems;
        });

        console.log(`Auftragsposition mit ID ${orderItemId} erfolgreich entfernt.`);
      } else {
        console.error(`Fehler beim Entfernen der Auftragsposition: Unerwarteter Status ${response.status}`);
      }
    } catch (error) {
      // Detaillierte Fehlerbehandlung und Logausgabe
      console.error('Fehler beim Entfernen der Auftragsposition:', error.message || error);

      // Benutzer über den Fehler informieren
      alert('Fehler: Die Auftragsposition konnte nicht entfernt werden. Bitte versuchen Sie es später erneut.');
    }
  };

  // Funktion zum Abrufen der Auftragspositionen
  const fetchOrderItems = async () => {
  try {
    const response = await api.get(`/orders/${orderId}/orderitems`);
    if (!Array.isArray(response.data)) return;

    // Jedes Item bekommt eine eindeutige tempId und manualPriceActive-Flag
     // Einfach Items übernehmen und nach position sortieren
const itemsFromServer = response.data.map((item) => {
  const { total, ...rest } = item; // ✅ OfferForm-Style: total NICHT im State halten

  return {
    ...rest,
    designPosition:
      (rest.designPosition === null || rest.designPosition === undefined)
        ? rest.position
        : rest.designPosition,
    tempId: rest.id,
    manualPriceActive: !!rest.manualPriceActive,
  };
});

setOrderItems(
  itemsFromServer.sort((a, b) => a.designPosition - b.designPosition)
);
  } catch (error) {
    console.error('Fehler beim Abrufen der Auftragspositionen:', error);
  }
};

  // 1. Auftragspositionen abrufen, wenn order.id sich ändert
  useEffect(() => {
    if (order.id) {
      fetchOrderItems(); // Funktion wird jetzt global aufgerufen
      fetchUnits(); // Einheiten abrufen (falls benötigt)
    }
  }, [order.id]);

 

  // 3. Artikel laden (nur beim ersten Laden der Seite)
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await api.get('/items');
        // Nur Artikel mit deactivated = false
        const activeItems = response.data.filter(item => item.deactivated === false);
        setItems(activeItems);
      } catch (error) {
        console.error('Fehler beim Laden der Artikel:', error);
      }
    };
    fetchItems();
  }, []);



  useEffect(() => {
    if (orderItems.length > 0) {
      // Beispiel: Setze den globalen Zustand, wenn mindestens ein OrderItem includeSaturday = true hat.
      // (Anpassen – du kannst hier auch andere Logik verwenden, falls nötig.)
      setTempIncludeSaturday(orderItems.some(item => item.includeSaturday));
      setTempIncludeSunday(orderItems.some(item => item.includeSunday));
      setTempIncludeHolidays(orderItems.some(item => item.includeHolidays));
    }
  }, [orderItems]);

  useEffect(() => {
    const fetchWorkshopOrders = async () => {
      try {
        const response = await api.get(`/orders/${orderId}/workshoporders`);
        setWorkshopOrders(response.data);
      } catch (error) {
        console.error("Fehler beim Laden der Werkstattaufträge:", error);
      }
    };
    if (orderId) {
      fetchWorkshopOrders();
    }
  }, [orderId]);



  // Auftragsposition hinzufügen
  const addOrderItem = async () => {
    try {
      if (!selectedItemId || selectedQuantity <= 0) {
        alert('Bitte wählen Sie einen Artikel aus und geben Sie eine gültige Menge an.');
        console.warn("Ungültige Eingabedaten: ", { selectedItemId, selectedQuantity });
        return;
      }

      const item = items.find((item) => item.id === selectedItemId);
      if (!item) {
        alert('Ausgewählter Artikel existiert nicht.');
        return;
      }

      // Bestimme die finale Einheit basierend auf itemType
      let finalUnitId = null;

      switch (item.itemType) {
        case 'insurance':
          // Versicherungsartikel => standardmäßig 33 (Prozent)
          finalUnitId = 33;
          break;
        
        case 'insuranceFixed':
  finalUnitId = 29; // oder die echte "EUR"-Einheit aus deiner DB
  break;

        case 'rental':
          // Mietartikel => Fallback aus typePricings oder Transport
          finalUnitId =
            item.machineType?.typePricings?.[0]?.unit?.id ||
            item.transportPricings?.[0]?.unit?.id ||
            29; // oder 27 - je nachdem was du als Default willst (Stunde / Tag)
          break;

        case 'transport':
          // Transportartikel => Falls du TransportPricing hast
          finalUnitId = item.transportPricings?.[0]?.unit?.id || 34; 
          // 34 wäre z.B. "pauschal" – passe das an dein System an
          break;

        case 'sparePart':
        case 'service':
        case 'sale':
          // Ersatzteil / Service / Verkauf => aus salesPricings
          finalUnitId = item.salesPricings?.[0]?.unitId || null;
          break;
          
        default:
          // Falls es noch andere Typen gibt
          console.log(`Kein Fallback definiert für itemType='${item.itemType}'.`);
          finalUnitId = null;
          break;
      }

      // Falls finalUnitId immer noch null ist, wirf einen Fehler oder setze einen Default
      if (!finalUnitId) {
        console.warn(`Keine passende Einheit für itemType='${item.itemType}' gefunden; bitte manuell zuweisen.`);
        // finalUnitId = 29; // z.B. Tag als letzter Ausweg
      }

      const payload = {
        orderId: order.id, // ID des aktuellen Auftrags
        itemId: selectedItemId, // ID des ausgewählten Artikels
        quantity: selectedQuantity, // Vom Benutzer eingegebene Menge
        unitId: finalUnitId,  // Einheit setzen
        rentalPeriodFrom: null,    // <-- nun ok für nullable TIMESTAMP
        rentalPeriodTo:   null,    // <-- ditto
        rentalTimeFrom:   null,
        rentalTimeTo:     null,
        // Hier werden die globalen Zustände übernommen:
        includeSaturday: tempIncludeSaturday,
        includeSunday: tempIncludeSunday,
        includeHolidays: tempIncludeHolidays,
      };

      console.log("Daten für den POST-Request:", payload);

       // 1) Haupt- und Linked-Items anlegen
    const { mainOrderItem, linkedOrderItems } = await api
      .post(`/orders/${orderId}/orderitems`, payload)
      .then(res => res.data);

    // 2) Haupt-Item komplett mit Includes nachladen
    const fullMain = await api
      .get(`/orders/${orderId}/orderitems/${mainOrderItem.id}`)
      .then(res => res.data);

    // 3) Verknüpfte Items komplett mit Includes nachladen
    const fullLinked = await Promise.all(
      linkedOrderItems.map(li =>
        api
          .get(`/orders/${orderId}/orderitems/${li.id}`)
          .then(res => res.data)
      )
    );

    // nach fullMain und fullLinked geholt:
const mainWithTemp = {
  ...fullMain,
  tempId: uuidv4(),         // neu
  manualPriceActive: false,
};
const linkedWithTemp = fullLinked.map(item => ({
  ...item,
  tempId: uuidv4(),         // neu
  manualPriceActive: false,
}));

   // 4) Haupt- und Linked-Items ans Ende hängen und nach position sortieren
  // Haupt- und Linked-Items ans Ende hängen und nach position sortieren
setOrderItems(prev =>
  [
    ...prev,
    mainWithTemp,
    ...linkedWithTemp,
  ]
    .map((it) => {
      const { total, ...rest } = it;   // ✅ falls Server total mitsendet
      return rest;
    })
    .sort((a, b) => a.position - b.position)
);


    // 6) Eingaben zurücksetzen
    setSelectedItemId(null);
    setSelectedQuantity(1);

  } catch (error) {
    console.error('Fehler beim Hinzufügen der Angebotsposition:', error);
  }
};



  // Funktion zur globalen Aktualisierung der Checkbox-Zustände in allen orderItems
  const handleGlobalCheckboxChange = async (field, value) => {
  try {
    if (field === 'includeSaturday') setTempIncludeSaturday(value);
    if (field === 'includeSunday') setTempIncludeSunday(value);
    if (field === 'includeHolidays') setTempIncludeHolidays(value);

    const updatedItems = orderItems.map(item => ({
      ...item,
      [field]: value,
    }));

    setOrderItems(
  updatedItems.map(({ total, ...rest }) => rest) // ✅ total raus
);


    // ✅ Backend: nur die 3 Flags schicken, kein komplettes Objekt
    const updatePromises = updatedItems.map((item) =>
      api.put(`/orders/${orderId}/orderitems/${item.id}`, {
        includeSaturday: item.includeSaturday,
        includeSunday: item.includeSunday,
        includeHolidays: item.includeHolidays,
      })
    );

    await Promise.all(updatePromises);

    // ✅ Bis-Datum neu rechnen (nur rentals)
    const rentals = updatedItems.filter(i => i.item?.itemType === "rental");
    updateRentalPeriodTo(rentals);

  } catch (error) {
    console.error(`Fehler beim Aktualisieren von ${field}:`, error);
  }
};



  // === ganz unten in OfferForm.js, nach Deinem useEffect fürs offerItems-Update ===
  // immer dann neu berechnen, wenn sich includeSaturday/includeSunday/includeHolidays
  // für genau diejenige Zeile ändern, die gerade im Bearbeitungsmodus ist:
 const currentEditingItem = orderItems.find(i => i.id === editingRowId);

useEffect(() => {
  if (!editingRowId || !currentEditingItem) return;
  updateRentalPeriodTo([currentEditingItem]);
}, [
  editingRowId,
  currentEditingItem?.includeSaturday,
  currentEditingItem?.includeSunday,
  currentEditingItem?.includeHolidays,
  currentEditingItem?.rentalPeriodFrom,
  currentEditingItem?.quantity,
  currentEditingItem?.unitId,
  updateRentalPeriodTo,
]);





  // Einheiten abrufen und im Zustand speichern
  const fetchUnits = async () => {
    try {
      const response = await api.get('/units'); // Beispiel-Endpoint zum Abrufen der Einheiten
      console.log("Geladene Einheiten:", response.data);
      setUnits(response.data); // Setze den Zustand für die Einheiten
    } catch (error) {
      console.error('Fehler beim Abrufen der Einheiten:', error);
    }
  };



  // Abbrechen der Bearbeitung
  const handleEditClick = (rowId) => {
    if (!orderItems || orderItems.length === 0) {
      console.warn("Keine OrderItems verfügbar, um den Bearbeitungsmodus zu starten.");
      return;
    }

    console.log("Speichern der ursprünglichen OrderItems vor Bearbeitung:", orderItems);

    // Speichere die ursprünglichen OrderItems als Kopie
    setOriginalOrderItems([...orderItems]);

    // Finde das OrderItem, das bearbeitet wird, und setze die temporären Zustände
    const itemToEdit = orderItems.find(item => item.id === rowId);
    if (itemToEdit) {
      console.log("Bearbeitung starten für:", itemToEdit);
      setSelectedItemId(itemToEdit.itemId);
      setSelectedQuantity(itemToEdit.quantity);

      // Temporäre Zustände für die Mietzeitfelder des orderItems setzen
      setTempRentalPeriodFrom(itemToEdit.rentalPeriodFrom || "");
      setTempRentalPeriodTo(itemToEdit.rentalPeriodTo || "");
      setTempRentalTimeFrom(itemToEdit.rentalTimeFrom || "");
      setTempRentalTimeTo(itemToEdit.rentalTimeTo || "");

      // Initialisiere die temporären Checkboxwerte mit den orderItem-Werten
      setTempIncludeSaturday(itemToEdit.includeSaturday);
      setTempIncludeSunday(itemToEdit.includeSunday);
      setTempIncludeHolidays(itemToEdit.includeHolidays);
    } else {
      console.warn(`Kein OrderItem mit der ID ${rowId} gefunden.`);
    }

    setEditingRowId(rowId);
  };


  const handleCancelEdit = () => {
    console.log("Abbrechen gedrückt. Original OrderItems:", originalOrderItems);

    if (!originalOrderItems || originalOrderItems.length === 0) {
      console.warn("Keine Originaldaten zum Zurücksetzen gefunden.");
      return;
    }

    // Setze orderItems auf die ursprünglichen Werte zurück
    setOrderItems([...originalOrderItems]);
    console.log("OrderItems nach Abbrechen zurückgesetzt auf:", originalOrderItems);

    // Beende den Bearbeitungsmodus
    setEditingRowId(null);
  };

  const calculatePrice = (itemObj, quantity) => {
    let pricePerUnit = 0;
    let total = 0;

    if (itemObj.item?.machineType?.typePricings) {
      // Beispielhafte Preisstaffelungslogik
      const pricing = itemObj.item.machineType.typePricings.find(
        (p) => quantity >= p.minQuantity && quantity <= p.maxQuantity
      );
      if (pricing) {
        pricePerUnit = parseFloat(pricing.pricePerUnit);
        total = parseFloat((pricePerUnit * quantity).toFixed(2));
      }
    } else if (itemObj.item?.transportPricings?.[0]?.pricePerUnit) {
      pricePerUnit = parseFloat(itemObj.item.transportPricings[0].pricePerUnit);
      total = parseFloat((pricePerUnit * quantity).toFixed(2));
    }

    return { pricePerUnit, total };
  };

const fetchFilteredItems = useCallback(
  debounce(async (searchTerm) => {
    try {
      const response = await api.get(`/items?search=${searchTerm}`);
      const activeItems = response.data.filter(item => item.deactivated === false);
      setItems(activeItems);
    } catch (error) {
      console.error('Fehler beim Filtern der Artikel:', error);
    }
  }, 300),
  []
);

useEffect(() => {
  return () => fetchFilteredItems.cancel?.();
}, [fetchFilteredItems]);


  const handleItemSearchChange = (inputValue) => {
    if (inputValue.trim() === '') {
      setItems([]); // Leere Liste, wenn keine Eingabe
      return;
    }

    fetchFilteredItems(inputValue); // Filtere Artikel basierend auf dem Suchbegriff
  };

  // Optionen für `react-select` transformieren
  const allowedTypes = ['rental', 'sale', 'service', 'sparePart', 'transport']; 
const filteredItems = items.filter(item => allowedTypes.includes(item.itemType));

const itemOptions = filteredItems.map(item => ({
  value: item.id,
  label: formatItemOptionLabel(item)
}));

  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [lwOperatorContacts, setLWOperatorContacts] = useState([]); // State für Ansprechpartner vor Ort (LW-Bediener)
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedLWOperator, setSelectedLWOperator] = useState(null); // State für den ausgewählten Ansprechpartner vor Ort
  const [showNewLWOperatorDialog, setShowNewLWOperatorDialog] = useState(false);
  const [newLWOperator, setNewLWOperator] = useState(EMPTY_CONTACT);
  const [newLWOperatorError, setNewLWOperatorError] = useState('');
  const [isSavingLWOperator, setIsSavingLWOperator] = useState(false);
  const [showNewBestellerDialog, setShowNewBestellerDialog] = useState(false);
  const [newBesteller, setNewBesteller] = useState(EMPTY_CONTACT);
  const [newBestellerError, setNewBestellerError] = useState('');
  const [isSavingBesteller, setIsSavingBesteller] = useState(false);
  const [contactToEdit, setContactToEdit] = useState(null);
  const [editContactError, setEditContactError] = useState('');
  const [isSavingEditedContact, setIsSavingEditedContact] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [contactsLoadedForCustomer, setContactsLoadedForCustomer] = useState(null);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // Kunden laden (nur einmal beim initialen Rendern)
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        console.log("Kunden werden geladen...");
        const customersResponse = await api.get("/customers");
        const loadedCustomers = customersResponse.data;
        setCustomers(loadedCustomers);
        setFilteredCustomers(loadedCustomers); // Zu Beginn alle Kunden anzeigen
        console.log("Kunden geladen:", loadedCustomers);
      } catch (error) {
        console.error("Fehler beim Laden der Kunden:", error);
      }
    };

    fetchCustomers();
  }, []);

  // Funktion, um die Kontakte eines bestimmten Kunden zu laden
  const loadContactsForCustomer = useCallback(async (customerId, contactId = null) => {
    try {
      if (!customerId || customerId === contactsLoadedForCustomer) return;

      console.log(`Lade Kontakte für Kunde mit ID: ${customerId}`);
      const response = await api.get(
        `/contacts/customer/${customerId}?contactFunction=Besteller`
      );
      setCustomerContacts(response.data);
      setContactsLoadedForCustomer(customerId);
      console.log("Kontakte geladen:", response.data);

      if (contactId) {
        const selectedContact = response.data.find(
          (contact) => contact.id.toString() === contactId.toString()
        );
        if (selectedContact) {
          setSelectedContact(selectedContact);
          console.log("Ausgewählter Besteller gesetzt:", selectedContact);
        }
      }
    } catch (error) {
      console.error("Fehler beim Laden der Besteller:", error);
    }
  }, [contactsLoadedForCustomer]);

  // Funktion, um die LW-Bediener-Kontakte eines bestimmten Kunden zu laden
  const loadLWOperatorContacts = useCallback(async (customerId, lwOperatorId = null) => {
    try {
      if (!customerId) return;

      console.log(`Lade LW-Bediener für Kunde mit ID: ${customerId}`);
      const response = await api.get(
        `/contacts/customer/${customerId}?contactFunction=LW-Bediener`
      );
      setLWOperatorContacts(response.data);
      console.log("LW-Bediener geladen:", response.data);

      if (lwOperatorId) {
        const selectedLWOperator = response.data.find(
          (contact) => contact.id.toString() === lwOperatorId.toString()
        );
        if (selectedLWOperator) {
          setSelectedLWOperator(selectedLWOperator);
          console.log("Ausgewählter Ansprechpartner vor Ort gesetzt:", selectedLWOperator);
        }
      }
    } catch (error) {
      console.error("Fehler beim Laden der LW-Bediener:", error);
    }
  }, [setLWOperatorContacts]);

  // Auftrag und zugehörige Daten laden, falls im Bearbeitungsmodus
  useEffect(() => {
    if (mode === "edit" && orderId && customers.length > 0 && !initialDataLoaded) {
      const fetchOrderData = async () => {
        try {
          console.log("Auftrag wird geladen...");
          const orderResponse = await api.get(`/orders/${orderId}`);
          const fetchedOrder = orderResponse.data;

          const timeNoSeconds = fetchedOrder.deliveryTime
   ? fetchedOrder.deliveryTime.slice(0, 5)
   : null;

          console.log("Geladener Auftrag:", fetchedOrder);

          setOrder({
            ...fetchedOrder,
            deliveryTime: timeNoSeconds,
            rentalPeriodFrom: normalizeDate(fetchedOrder.rentalPeriodFrom),
            rentalPeriodTo: normalizeDate(fetchedOrder.rentalPeriodTo),
            includeLaddersInEmail: !!fetchedOrder.includeLaddersInEmail,
          });

          console.log("Auftrag gesetzt:", fetchedOrder);

          if (fetchedOrder.customerId) {
            const selectedCustomer = customers.find(
              (customer) => customer.id.toString() === fetchedOrder.customerId.toString()
            );
            if (selectedCustomer) {
              setSelectedCustomer(selectedCustomer);
              console.log("Ausgewählter Kunde gesetzt:", selectedCustomer);
              // Kontakte und LW-Bediener nur einmal laden
              await loadContactsForCustomer(fetchedOrder.customerId, fetchedOrder.contactId);
              await loadLWOperatorContacts(fetchedOrder.customerId, fetchedOrder.lwOperatorId);
            }
          }
        } catch (error) {
          console.error("Fehler beim Laden des Auftrags:", error);
        } finally {
          setInitialDataLoaded(true);
          setIsLoading(false);
        }
      };

      fetchOrderData();
    } else {
      setIsLoading(false);
    }
  }, [mode, orderId, customers, initialDataLoaded, loadContactsForCustomer, loadLWOperatorContacts]);

  // Funktion, um das Datum zu normalisieren
  const normalizeDate = (dateString) => dateString?.split("T")[0] || "";

  // Funktion zum Filtern der Kunden (debounced, um unnötige Anfragen zu vermeiden)
  const filterCustomers = debounce((inputValue) => {
    if (inputValue.trim() === "") {
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter((customer) =>
        `${customer.company} ${customer.customerNumber} ${customer.firstName} ${customer.lastName} ${customer.street} ${customer.postalCode} ${customer.city}`
          .toLowerCase()
          .includes(inputValue.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  }, 300);

  // Handle search field changes
  const handleCustomerSearchChange = (inputValue) => {
    filterCustomers(inputValue);
  };

  // Auftrag auswählen und alle zugehörigen Daten laden
  const handleCustomerChange = (selectedOption) => {
    if (!selectedOption) {
      setOrder((prev) => ({
        ...prev,
        customerId: "",
        contactId: "", // Kontakt ebenfalls zurücksetzen
        lwOperatorId: "", // Ansprechpartner vor Ort zurücksetzen
      }));
      setSelectedCustomer(null);
      setCustomerContacts([]);
      setSelectedContact(null);
      setLWOperatorContacts([]);
      setSelectedLWOperator(null);
      return;
    }

    const selectedCustomerId = selectedOption.value;

    // Den neuen Kunden setzen und Besteller zurücksetzen
    setOrder((prev) => ({
      ...prev,
      customerId: selectedCustomerId,
      contactId: "", // Zurücksetzen des Bestellers, da der Kunde geändert wurde
      lwOperatorId: "", // Zurücksetzen des LW-Bedieners
    }));
    setSelectedContact(null);
    setSelectedLWOperator(null);

    // Den ausgewählten Kunden im Zustand speichern und anzeigen
    const selected = customers.find(
      (customer) => customer.id.toString() === selectedCustomerId.toString()
    );
    if (selected) {
      setSelectedCustomer(selected);
      console.log("Ausgewählter Kunde geändert:", selected);
      // Kontakte für den neuen Kunden laden
      loadContactsForCustomer(selectedCustomerId);
      loadLWOperatorContacts(selectedCustomerId);
    } else {
      setSelectedCustomer(null);
      console.log("Kunde konnte nicht gefunden werden");
    }
  };

   const handleOpenCustomerEdit = () => {
  if (!order.customerId) return;
  navigate(`/customerform/${order.customerId}`);
};



  // Funktion zum Aktualisieren von Feldern (z.B. date, deliveryTime, etc.)
  const handleChange = (e) => {
    const { name, value } = e.target;

    setOrder(prev => {
      const updatedOrder = {
        ...prev,
        [name]:
     name === "deliveryTime"
       ? (value && value.length > 0 ? value : null)
       : value, // Aktualisiere das geänderte Feld
      };

      // Wenn rentalPeriodFrom geändert wurde, berechne rentalPeriodTo


      return updatedOrder;
    });
  };

  const handleOrderNoteTextBlockChange = (event) => {
    const selectedId = event.target.value;
    setSelectedOrderNoteTextBlockId(selectedId);
    if (!selectedId) return;

    const selectedTextBlock = orderNoteTextBlocks.find(
      textBlock => String(textBlock.id) === String(selectedId)
    );
    if (!selectedTextBlock?.content) {
      setSelectedOrderNoteTextBlockId('');
      return;
    }

    setOrder(prev => {
      const existingNotes = String(prev.notes ?? '');
      const textToInsert = String(selectedTextBlock.content);
      let separator = '';

      if (existingNotes.length > 0) {
        separator = existingNotes.endsWith('\n\n')
          ? ''
          : existingNotes.endsWith('\n')
            ? '\n'
            : '\n\n';
      }

      return {
        ...prev,
        notes: `${existingNotes}${separator}${textToInsert}`,
      };
    });

    setSelectedOrderNoteTextBlockId('');
  };

  // Besteller ändern
  const handleContactChange = (e) => {
    const selectedContactId = e.target.value;
    setOrder((prev) => ({
      ...prev,
      contactId: selectedContactId,
    }));

    const selected = customerContacts.find(
      (contact) => contact.id.toString() === selectedContactId.toString()
    );
    if (selected) {
      setSelectedContact(selected);
      console.log("Ausgewählter Besteller geändert:", selected);
    } else {
      setSelectedContact(null);
      console.log("Besteller konnte nicht gefunden werden");
    }
  };

  // Ansprechpartner vor Ort ändern
  const handleLWOperatorChange = (e) => {
    const selectedLWOperatorId = e.target.value;
    setOrder((prev) => ({
      ...prev,
      lwOperatorId: selectedLWOperatorId,
    }));

    const selected = lwOperatorContacts.find(
      (contact) => contact.id.toString() === selectedLWOperatorId.toString()
    );
    if (selected) {
      setSelectedLWOperator(selected);
      console.log("Ausgewählter Ansprechpartner vor Ort geändert:", selected);
    } else {
      setSelectedLWOperator(null);
      console.log("Ansprechpartner vor Ort konnte nicht gefunden werden");
    }
  };

  const openEditContactDialog = (contact) => {
    if (!contact) return;

    setContactToEdit({
      ...contact,
      contactFirstName: contact.contactFirstName || '',
      contactLastName: contact.contactLastName || '',
      contactPhone: contact.contactPhone || '',
      contactMobile: contact.contactMobile || '',
      contactEmail: contact.contactEmail || '',
    });
    setEditContactError('');
  };

  const closeEditContactDialog = () => {
    if (isSavingEditedContact) return;
    setContactToEdit(null);
    setEditContactError('');
  };

  const handleEditContactChange = (e) => {
    const { name, value } = e.target;
    setContactToEdit(prev => prev ? { ...prev, [name]: value } : prev);
    if (editContactError) setEditContactError('');
  };

  const sortContactsByName = (contacts) => [...contacts].sort((a, b) =>
    `${a.contactLastName || ''} ${a.contactFirstName || ''}`.localeCompare(
      `${b.contactLastName || ''} ${b.contactFirstName || ''}`,
      'de'
    )
  );

  const saveEditedContact = async () => {
    if (!contactToEdit?.id) return;

    const payload = {
      contactFirstName: contactToEdit.contactFirstName.trim(),
      contactLastName: contactToEdit.contactLastName.trim(),
      contactPhone: contactToEdit.contactPhone.trim(),
      contactMobile: contactToEdit.contactMobile.trim(),
      contactEmail: contactToEdit.contactEmail.trim(),
      contactFunction: contactToEdit.contactFunction,
      // Diese Felder werden im Auftragsdialog nicht bearbeitet, müssen beim PUT
      // aber erhalten bleiben.
      crmRole: contactToEdit.crmRole ?? null,
      isMainContact: Boolean(contactToEdit.isMainContact),
      notes: contactToEdit.notes ?? null,
      birthday: contactToEdit.birthday ?? null,
    };

    if (!payload.contactFirstName || !payload.contactLastName) {
      setEditContactError('Vorname und Nachname sind erforderlich.');
      return;
    }

    setIsSavingEditedContact(true);
    setEditContactError('');

    try {
      const response = await api.put(`/contacts/${contactToEdit.id}`, payload);
      const updatedContact = response.data;
      const isBesteller = String(updatedContact.contactFunction || '').toLowerCase() === 'besteller';

      if (isBesteller) {
        setCustomerContacts(prev => sortContactsByName(
          prev.map(contact => contact.id === updatedContact.id ? updatedContact : contact)
        ));
        setSelectedContact(updatedContact);
      } else {
        setLWOperatorContacts(prev => sortContactsByName(
          prev.map(contact => contact.id === updatedContact.id ? updatedContact : contact)
        ));
        setSelectedLWOperator(updatedContact);
      }

      setContactToEdit(null);
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Kontakts:', error);
      setEditContactError(
        error.response?.data?.details ||
        error.response?.data?.error ||
        'Der Kontakt konnte nicht aktualisiert werden.'
      );
    } finally {
      setIsSavingEditedContact(false);
    }
  };

  const openNewLWOperatorDialog = () => {
    if (!order.customerId) {
      alert('Bitte zuerst einen Kunden auswählen.');
      return;
    }

    setNewLWOperator(EMPTY_CONTACT);
    setNewLWOperatorError('');
    setShowNewLWOperatorDialog(true);
  };

  const closeNewLWOperatorDialog = () => {
    if (isSavingLWOperator) return;
    setShowNewLWOperatorDialog(false);
    setNewLWOperatorError('');
  };

  const handleNewLWOperatorChange = (e) => {
    const { name, value } = e.target;
    setNewLWOperator(prev => ({ ...prev, [name]: value }));
    if (newLWOperatorError) setNewLWOperatorError('');
  };

  const findExistingContact = (contacts, payload) => {
    const normalize = value => String(value || '').trim().toLocaleLowerCase('de-DE');

    return contacts.find(contact => {
      const sameName =
        normalize(contact.contactFirstName) === normalize(payload.contactFirstName) &&
        normalize(contact.contactLastName) === normalize(payload.contactLastName);
      if (!sameName) return false;

      const enteredContactValues = [
        ['contactEmail', payload.contactEmail],
        ['contactPhone', payload.contactPhone],
        ['contactMobile', payload.contactMobile],
      ].filter(([, value]) => normalize(value));

      return enteredContactValues.length === 0 || enteredContactValues.some(
        ([field, value]) => normalize(contact[field]) === normalize(value)
      );
    });
  };

  const saveNewLWOperator = async () => {
    const payload = {
      contactFirstName: newLWOperator.contactFirstName.trim(),
      contactLastName: newLWOperator.contactLastName.trim(),
      contactPhone: newLWOperator.contactPhone.trim(),
      contactMobile: newLWOperator.contactMobile.trim(),
      contactEmail: newLWOperator.contactEmail.trim(),
      contactFunction: 'LW-Bediener',
    };

    if (!payload.contactFirstName || !payload.contactLastName) {
      setNewLWOperatorError('Vorname und Nachname sind erforderlich.');
      return;
    }

    const existingContact = findExistingContact(lwOperatorContacts, payload);

    if (existingContact) {
      setOrder(prev => ({ ...prev, lwOperatorId: existingContact.id }));
      setSelectedLWOperator(existingContact);
      setShowNewLWOperatorDialog(false);
      return;
    }

    setIsSavingLWOperator(true);
    setNewLWOperatorError('');

    try {
      const response = await api.post(`/contacts/${order.customerId}`, payload);
      const createdContact = response.data;

      setLWOperatorContacts(prev => [...prev, createdContact].sort((a, b) =>
        `${a.contactLastName} ${a.contactFirstName}`.localeCompare(
          `${b.contactLastName} ${b.contactFirstName}`,
          'de'
        )
      ));
      setSelectedLWOperator(createdContact);
      setOrder(prev => ({ ...prev, lwOperatorId: createdContact.id }));
      setShowNewLWOperatorDialog(false);
      setNewLWOperator(EMPTY_CONTACT);
    } catch (error) {
      console.error('Fehler beim Anlegen des Vor-Ort-Ansprechpartners:', error);
      setNewLWOperatorError(
        error.response?.data?.details ||
        error.response?.data?.error ||
        'Der Ansprechpartner konnte nicht angelegt werden.'
      );
    } finally {
      setIsSavingLWOperator(false);
    }
  };

  const openNewBestellerDialog = () => {
    if (!order.customerId) {
      alert('Bitte zuerst einen Kunden auswählen.');
      return;
    }

    setNewBesteller(EMPTY_CONTACT);
    setNewBestellerError('');
    setShowNewBestellerDialog(true);
  };

  const closeNewBestellerDialog = () => {
    if (isSavingBesteller) return;
    setShowNewBestellerDialog(false);
    setNewBestellerError('');
  };

  const handleNewBestellerChange = (e) => {
    const { name, value } = e.target;
    setNewBesteller(prev => ({ ...prev, [name]: value }));
    if (newBestellerError) setNewBestellerError('');
  };

  const saveNewBesteller = async () => {
    const payload = {
      contactFirstName: newBesteller.contactFirstName.trim(),
      contactLastName: newBesteller.contactLastName.trim(),
      contactPhone: newBesteller.contactPhone.trim(),
      contactMobile: newBesteller.contactMobile.trim(),
      contactEmail: newBesteller.contactEmail.trim(),
      contactFunction: 'Besteller',
    };

    if (!payload.contactFirstName || !payload.contactLastName) {
      setNewBestellerError('Vorname und Nachname sind erforderlich.');
      return;
    }

    const existingContact = findExistingContact(customerContacts, payload);
    if (existingContact) {
      setOrder(prev => ({ ...prev, contactId: existingContact.id }));
      setSelectedContact(existingContact);
      setShowNewBestellerDialog(false);
      return;
    }

    setIsSavingBesteller(true);
    setNewBestellerError('');

    try {
      const response = await api.post(`/contacts/${order.customerId}`, payload);
      const createdContact = response.data;

      setCustomerContacts(prev => [...prev, createdContact].sort((a, b) =>
        `${a.contactLastName} ${a.contactFirstName}`.localeCompare(
          `${b.contactLastName} ${b.contactFirstName}`,
          'de'
        )
      ));
      setSelectedContact(createdContact);
      setOrder(prev => ({ ...prev, contactId: createdContact.id }));
      setShowNewBestellerDialog(false);
      setNewBesteller(EMPTY_CONTACT);
    } catch (error) {
      console.error('Fehler beim Anlegen des Bestellers:', error);
      setNewBestellerError(
        error.response?.data?.details ||
        error.response?.data?.error ||
        'Der Besteller konnte nicht angelegt werden.'
      );
    } finally {
      setIsSavingBesteller(false);
    }
  };

  // Funktion, um Gesamtsummen zu berechnen und im Backend zu speichern
  const saveOrderTotals = async () => {
  const totalNet = orderItems.reduce((sum, row) => sum + calcRowTotal(row, orderItems), 0);
  const vatRate = Number(order.vatRate ?? 19);
  const vatAmount = totalNet * (vatRate / 100);
  const totalGross = totalNet + vatAmount;

  try {
    await api.put(`/orders/${order.id}`, {
      taxTreatment: order.taxTreatment,
        taxNote: order.taxNote,
        totalNet,
      vatAmount,
      totalGross,
    });
    console.log('Gesamtsummen erfolgreich gespeichert.');
  } catch (error) {
    console.error('Fehler beim Speichern der Gesamtsummen:', error);
    alert(error.response?.data?.error || 'Fehler beim Speichern der Gesamtsummen.');
      return false;
  }
};


  const updateOrderAndSaveTotals = async () => {
  // ✅ OfferForm-Style: niemals total mitsenden
  const sanitized = orderItems.map((i) => ({
  id: i.id,
  quantity: i.quantity,
  unitId: i.unitId,
  rentalPeriodFrom: i.rentalPeriodFrom,
  rentalTimeFrom: i.rentalTimeFrom,
  rentalTimeTo: i.rentalTimeTo,
  includeSaturday: i.includeSaturday,
  includeSunday: i.includeSunday,
  includeHolidays: i.includeHolidays,
  pricePerUnit: i.pricePerUnit,
  manualPriceActive: i.manualPriceActive,
  description: i.description,
  designPosition: i.designPosition,
  mainItemId: i.mainItemId,
}));

  await api.put(`/orders/${order.id}/orderitems`, sanitized);

  const totalNet = orderItems.reduce((sum, row) => sum + calcRowTotal(row, orderItems), 0);
  const vatRate = Number(order.vatRate ?? 19);
  const vatAmount = totalNet * (vatRate / 100);
  const totalGross = totalNet + vatAmount;

  try {
    await api.put(`/orders/${order.id}`, { taxTreatment: order.taxTreatment,
        taxNote: order.taxNote,
        totalNet, vatAmount, totalGross });
    console.log('Gesamtsummen erfolgreich gespeichert.');
  } catch (error) {
    console.error('Fehler beim Speichern der Gesamtsummen:', error);
    alert(error.response?.data?.error || 'Fehler beim Speichern der Gesamtsummen.');
      return false;
  }
};


  const handleBackButtonClick = async () => {
    if (await saveOrderTotals() === false) return; // Speichern der Gesamtsummen
    navigate('/orders'); // Zurück zur Auftragsübersicht
  };

  // Auftrag speichern
const handleSave = async () => {
  if (!order.customerId) {
    alert("Bitte wählen Sie einen Kunden aus.");
    return;
  }

  try {
    const orderDate = order.date || new Date().toISOString();
    const validUntilDate = order.validUntil
      || new Date(new Date(orderDate).setDate(new Date(orderDate).getDate() + 14)).toISOString();

    // Falls der Auftrag noch "draft" ist, bei Save auf "pending" setzen, damit Nummer vergeben wird
    const finalStatus = order.status === 'draft' ? 'pending' : order.status;

    const payload = {
      ...order,
      date: orderDate,
      validUntil: validUntilDate,
      contactId: order.contactId || null,
      lwOperatorId: order.lwOperatorId || null,
      status: finalStatus,
      includeSaturday: order.includeSaturday,
      includeSunday: order.includeSunday,
      includeHolidays: order.includeHolidays,
    };

    // 1) Speichern und orderNumber ermitteln
    let orderNumber;
    if (mode === "edit") {
      const putRes = await api.put(`/orders/${orderId}`, payload);
      // nimm bevorzugt die Nummer aus der Antwort, fallback auf State
      orderNumber = putRes?.data?.orderNumber || order.orderNumber;
      alert("Auftrag erfolgreich aktualisiert!");
    } else {
      const postRes = await api.post("/orders", payload);
      orderNumber = postRes?.data?.orderNumber;
      alert(`Auftrag erfolgreich erstellt! Auftragsnummer: ${orderNumber}`);
    }

    // 2) Gesamtsummen speichern
    await saveOrderTotals();

    // 3) Optionaler Callback
    if (onSave) onSave();

    // 4) Zur Liste navigieren und die Nummer im State mitgeben (für Filter-Übernahme)
    navigate('/orders', { state: { focusOrderNumber: orderNumber } });
  } catch (error) {
    console.error("Fehler beim Speichern des Auftrags:", error);
      alert(error.response?.data?.error || "Der Beleg konnte nicht gespeichert werden.");
  }
};



  const moveOrderItemUp = (id) => {
  setOrderItems(prev => {
    let sorted = [...prev].sort((a, b) => (a.designPosition ?? a.position) - (b.designPosition ?? b.position));
    const idx = sorted.findIndex(i => i.id === id);
    if (idx > 0) {
      const temp = sorted[idx - 1].designPosition;
      sorted[idx - 1].designPosition = sorted[idx].designPosition;
      sorted[idx].designPosition = temp;
      saveOrderItemDesignPositions(sorted);   // <-- Jetzt aktiv!
    }
    return [...sorted];
  });
};

const moveOrderItemDown = (id) => {
  setOrderItems(prev => {
    let sorted = [...prev].sort((a, b) => (a.designPosition ?? a.position) - (b.designPosition ?? b.position));
    const idx = sorted.findIndex(i => i.id === id);
    if (idx < sorted.length - 1 && idx !== -1) {
      const temp = sorted[idx + 1].designPosition;
      sorted[idx + 1].designPosition = sorted[idx].designPosition;
      sorted[idx].designPosition = temp;
      saveOrderItemDesignPositions(sorted);   // <-- Jetzt aktiv!
    }
    return [...sorted];
  });
};

const saveOrderItemDesignPositions = async (items) => {
  try {
    // Sende alle OrderItems mit den neuen designPosition-Werten
    await api.put(`/orders/${order.id}/orderitems/designPositions`, 
      items.map(i => ({ id: i.id, designPosition: i.designPosition }))
    );
  } catch (error) {
    console.error('Fehler beim Speichern der Reihenfolge:', error);
  }
};

  // Kundenoptionen für React Select
  // Kundenoptionen für React Select (mit Sperr-Infos)  ⬅️ NEU
const customerOptions = filteredCustomers.map((customer) => ({
  value: customer.id,
  label: `${customer.company} (${customer.customerNumber})`,
  isBlocked: !!customer.isBlocked,                 // ⬅️ NEU
  blockedUntil: customer.blockedUntil || null,     // ⬅️ NEU
  blockReason: customer.blockReason || null,       // ⬅️ NEU
}));

  if (isLoading) {
    return <div>Daten werden geladen...</div>;
  }

// URL für die SmartRental-Auswertung (KEIN Hook)
const smartRentalActive = !!order.smartRental;
const orderNumberSafe = order?.orderNumber ? String(order.orderNumber).trim() : '';
const reportUrl =
  smartRentalActive && lwPartnerId && orderNumberSafe
    ? `https://www.liftworker.de/app/auftragsauswertung_${encodeURIComponent(String(lwPartnerId).trim())}_${encodeURIComponent(orderNumberSafe)}.pdf`
    : null;

const openSmartRentalReport = () => {
  if (!reportUrl) return;
  window.open(reportUrl, '_blank', 'noopener,noreferrer');
};


function oneLineAddressFromSetup(setup) {
  if (!setup) return '';
  const street = (setup.street || '').trim(); // Hausnummer ist bei dir im Straßenfeld enthalten
  const zip    = (setup.postalCode || '').trim();
  const city   = (setup.city || '').trim();
  const country= (setup.country || 'Deutschland').trim();
  return [street, [zip, city].filter(Boolean).join(' '), country]
    .filter(Boolean)
    .join(', ');
}

function buildGoogleMapsRoute(origin, destination) {
  const base = 'https://www.google.com/maps/dir/?api=1';
  return `${base}&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&travelmode=driving`;
}

function openGoogleRoute(companySetup, operationLocationRaw) {
  if (!operationLocationRaw) return;
  const origin = oneLineAddressFromSetup(companySetup);

  // Ziel: **nur** der Einsatzort-String (wie von dir gewünscht)
  let destination = String(operationLocationRaw).trim();
  if (destination && !/deutschland/i.test(destination)) {
    destination = `${destination}, Deutschland`; // leichte Robustheit
  }

  const url = buildGoogleMapsRoute(origin, destination);
  window.open(url, '_blank', 'noopener,noreferrer');
}



function buildGraphHopperTruckUrl({ origin, destination, locale = 'de' }) {
  const base = 'https://graphhopper.com/maps/';
  // GraphHopper Maps akzeptiert point=... mehrfach, auch mit Adress-Strings
  const params = new URLSearchParams();
  params.set('profile', 'truck');
  params.set('locale', locale);
  params.append('point', origin);      // z.B. "Musterstraße 1, 12345 Musterstadt, Deutschland"
  params.append('point', destination); // z.B. order.operationLocation
  return `${base}?${params.toString()}`;
}

function openTruckRoute(companySetup, operationLocationRaw) {
  if (!operationLocationRaw || !companySetup) return;
  const origin = [
    companySetup.street,
    [companySetup.postalCode, companySetup.city].filter(Boolean).join(' '),
    companySetup.country || 'Deutschland'
  ].filter(Boolean).join(', ');
  const destination = /deutschland/i.test(operationLocationRaw)
    ? operationLocationRaw
    : `${operationLocationRaw}, Deutschland`;

  window.open(buildGraphHopperTruckUrl({ origin, destination }), '_blank', 'noopener,noreferrer');
}

// === Summen (wie in OfferForm) ===
const totalNetCalc = orderItems.reduce((sum, row) => sum + calcRowTotal(row, orderItems), 0);
const vatRateCalc = (order.vatRate ?? 19);
const vatAmountCalc = totalNetCalc * (vatRateCalc / 100);
const totalGrossCalc = totalNetCalc + vatAmountCalc;

  return (
    <div key={orderId} className="order-form-container">
       <Helmet>
        <title>Auftrag bearbeiten</title>
        <meta name="description" content="Auftrag bearbeiten" />
      </Helmet>
      <h1>{mode === "edit" ? "Auftrag bearbeiten" : "Neuen Auftrag erstellen"}</h1>

       {mode === "edit" && (
      <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#555' }}>
        <div>
          <strong>Erstellt von:</strong>{' '}
          {order.creator
            ? `${order.creator.firstName} ${order.creator.lastName}`
            : 'Unbekannt'}{' '}
          am {formatDateTime(order.createdAt)}
        </div>
        <div>
          <strong>Zuletzt geändert von:</strong>{' '}
          {order.updator
            ? `${order.updator.firstName} ${order.updator.lastName}`
            : 'Unbekannt'}{' '}
          am {formatDateTime(order.updatedAt)}
        </div>
      </div>
    )}

      <div className="form-row">
        {/* Linke Spalte - Adressblock */}
        <div className="col">
          <label htmlFor="customerId" className="full-width">
  {/* Text + Icon bleiben im normalen Inline-Flow, werden von deinem vorhandenen text-align gesteuert */}
  <div style={{ marginBottom: 4 }}>
    Kunde auswählen:
    <button
      type="button"
      onClick={() => {
        if (!order.customerId) return;
        navigate(`/customerform/${order.customerId}`);
      }}
      disabled={!order.customerId}
      title={order.customerId ? 'Kunde bearbeiten' : 'Bitte zuerst einen Kunden wählen'}
      style={{
        marginLeft: 6,
        padding: 0,
        border: 'none',
        background: 'transparent',
        cursor: order.customerId ? 'pointer' : 'not-allowed',
        fontSize: 16,
        lineHeight: 1,
      }}
    >
      ✏️
    </button>
  </div>

  <Select
    id="customerId"
    name="customerId"
    options={customerOptions}
    onInputChange={handleCustomerSearchChange}
    onChange={handleCustomerChange}
    value={customerOptions.find(option => option.value === order.customerId) || null}
    className="select-box-cst"
    isClearable
    placeholder="Kunde suchen..."
    getOptionValue={(o) => String(o.value)}
    getOptionLabel={(o) => (o.isBlocked ? `🚫 ${o.label}` : o.label)}
    formatOptionLabel={(data) => (
      <span
        title={
          data.isBlocked
            ? (data.blockReason
                ? `Gesperrt${data.blockedUntil ? ` bis ${new Date(data.blockedUntil).toLocaleDateString('de-DE')}` : ''} – Grund: ${data.blockReason}`
                : `Gesperrt${data.blockedUntil ? ` bis ${new Date(data.blockedUntil).toLocaleDateString('de-DE')}` : ''}`
              )
            : ''
        }
      >
        {data.isBlocked ? '🚫 ' : ''}{data.label}
      </span>
    )}
    styles={{
      option: (base, { data, isFocused, isSelected }) => ({
        ...base,
        color: data.isBlocked ? '#b91c1c' : base.color,
        fontWeight: data.isBlocked ? 600 : 400,
        backgroundColor: isFocused
          ? (data.isBlocked ? '#fee2e2' : base.backgroundColor)
          : isSelected
            ? base.backgroundColor
            : 'transparent',
      }),
      singleValue: (base, { data }) => ({
        ...base,
        color: data?.isBlocked ? '#b91c1c' : base.color,
        fontWeight: data?.isBlocked ? 600 : 400,
      }),
    }}
  />
</label>




          {/* Adressblock - Anzeige der Kundendaten */}
          {selectedCustomer && (
  <div className="customer-details">
    {/* ⬇️ Warnbox bei gesperrtem Kunden */}
    {selectedCustomer.isBlocked && (                                              // ⬅️ NEU
      <div
        style={{
          background: '#FEF2F2',
          border: '1px solid #FECACA',
          color: '#B91C1C',
          padding: '8px 12px',
          borderRadius: 6,
          marginBottom: 10,
          fontWeight: 600,
        }}
      >
        🚫 Kunde ist gesperrt
        {selectedCustomer.blockedUntil
          ? ` bis ${new Date(selectedCustomer.blockedUntil).toLocaleDateString('de-DE')}`
          : ''}{selectedCustomer.blockReason ? ` – Grund: ${selectedCustomer.blockReason}` : ''}
      </div>
    )}
              <p><strong>Firma:</strong> {selectedCustomer.company}</p>
              <p><strong>Zusatz:</strong> {selectedCustomer.companyAddon}</p>
              <p><strong>Anrede:</strong> {selectedCustomer.salutation} {selectedCustomer.firstName} {selectedCustomer.lastName}</p>
              <p><strong>Adresse:</strong> {selectedCustomer.street}, {selectedCustomer.postalCode} {selectedCustomer.city}</p>
              <TaxTreatmentFields document={order} onChange={setOrder} customer={selectedCustomer} readOnly={Boolean(order.invoices?.length)} />

              {/* Button für TransportMarker */}
              <button
                type="button"
                onClick={() => navigate(`/orders/${orderId}/transportmarkers`)}
                style={{
                  marginTop: '10px',
                  padding: '10px 15px',
                  fontSize: '0.9em',
                  backgroundColor: '#007bff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  marginRight: '15px',
                }}
              >
                TransportMarker
              </button>

              {/* Neuer Button für ServiceMarker */}
    <button
      type="button"
      onClick={() => navigate(`/orders/${orderId}/servicemarkers`)}
      style={{
        marginTop: '10px',
        padding: '10px 15px',
        fontSize: '0.9em',
        backgroundColor: '#007bff',
        color: '#fff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
      }}
    >
      Servicemarker
    </button>

              

 <div className="checkbox-group" style={{ marginTop: '20px' }}>
  <div
    className="checkbox-list"
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px',
      marginTop: '20px'
    }}
  >
    {/* ⬇︎ SmartRental: Label + Button in einer Reihe */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <label htmlFor="smartRental" className="checkbox-item" style={{ margin: 0 }}>
        <input
          type="checkbox"
          id="smartRental"
          name="smartRental"
          checked={order.smartRental}
          onChange={e =>
            setOrder(prev => ({ ...prev, smartRental: e.target.checked }))
          }
        />
        SmartRental
      </label>

      {order.smartRental && (
        <button
          type="button"
          onClick={openSmartRentalReport}
          disabled={!reportUrl}
          title={
            !order?.orderNumber
              ? 'Auftragsnummer fehlt – Auftrag zuerst speichern.'
              : (!lwPartnerId
                ? 'lwLicensePartnerId fehlt im CompanySetup.'
                : 'Auftragsauswertung öffnen')
          }
          style={{
            fontSize: 12,
            padding: '4px 8px',
            borderRadius: 6,
            border: '1px solid #ddd',
            cursor: reportUrl ? 'pointer' : 'not-allowed',
            background: reportUrl ? '#9c28a7ff' : '#eee'
          }}
        >
          Auswertung
        </button>
      )}
    </div>

    <label htmlFor="obligationInstruction" className="checkbox-item">
      <input
        type="checkbox"
        id="obligationInstruction"
        name="obligationInstruction"
        checked={order.obligationInstruction}
        onChange={e =>
          setOrder(prev => ({ ...prev, obligationInstruction: e.target.checked }))
        }
      />
      Unterwpflicht
    </label>
  </div>
</div>

{/* Transportnotiz */}
<label htmlFor="notes" className="full-width" style={{ marginTop: '15px' }}>
              
              <textarea
                id="transportNotes"
                name="transportNotes"
                value={order.transportNotes ?? ""} 
                onChange={handleChange}
                className="textarea-notes"
                rows="6"
                style={{ width: '60%', marginBottom: '20px', marginTop: '20px' }} // Breite kann hier angepasst werden
                placeholder="Transportnotiz"
              />
            </label>




            </div>
          )}

        </div>

      
        {/* Mittlere Spalte - Mietzeitraum */}
        <div className="col">
          
          <div>
            

            {/* Anlieferzeit Feld */}
            <label htmlFor="deliveryTime" className="full-width">
              Anlieferzeit:
              <input
                type="time"
                id="deliveryTime"
                name="deliveryTime"
                step="60" 
                value={order.deliveryTime || ""}
                onChange={handleChange}
                className="input-box"
              />
            </label>

            {/* Einsatzort (operationLocation) */}
            <label htmlFor="operationLocation" className="full-width">
  Einsatzort:
  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
     <div style={{ flex: 1 }}>
<div ref={opLocWrapRef} style={{ position: 'relative', flex: 1 }}>
  <input
    type="text"
    value={opLocInput}
    onChange={onOpLocChange}
    onFocus={onOpLocFocus}
    onBlur={onOpLocBlur}
    onKeyDown={onOpLocKeyDown}
    placeholder="Straße Hausnr., PLZ Ort"
    style={{
      width: '100%',
      height: 40,
      borderRadius: 4,
      border: '1px solid #ccc',
      padding: '0 10px',
      marginBottom: 10
    }}
  />
  {opLocOpen && (
    <div
      style={{
        position: 'absolute',
        top: 42,
        left: 0,
        right: 0,
        zIndex: 50,
        background: '#fff',
        border: '1px solid #ddd',
        borderRadius: 4,
        maxHeight: 260,
        overflowY: 'auto',
        boxShadow: '0 6px 24px rgba(0,0,0,0.08)'
      }}
    >
      {opLocLoading && (
        <div style={{ padding: 8, fontStyle: 'italic', color: '#666' }}>Lade Vorschläge…</div>
      )}
      {!opLocLoading && opLocSuggest.length === 0 && (
        <div style={{ padding: 8, color: '#666' }}>Keine Vorschläge</div>
      )}
      {!opLocLoading && opLocSuggest.map((row, idx) => (
        <div
          key={`${row.label}-${idx}`}
          onMouseDown={() => selectOpLoc(row.label)}
          onMouseEnter={() => setOpLocIndex(idx)}
          style={{
            padding: '8px 10px',
            background: idx === opLocIndex ? '#f3f4f6' : '#fff',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
          title={row.label}
        >
          {row.label}
        </div>
      ))}
    </div>
  )}
</div>

</div>


    {/* NEU: Route-Button (ohne API/Key) */}
    <button
      type="button"
      onClick={() => openGoogleRoute(companySetup, order.operationLocation)}
      disabled={!companySetup || !order.operationLocation}
      title={
        !order.operationLocation
          ? 'Bitte zuerst den Einsatzort eintragen'
          : (!companySetup ? 'Firmenstandort (CompanySetup) fehlt' : 'Route in Google Maps öffnen')
      }
      style={{
        whiteSpace: 'nowrap',
        padding: '6px 10px',
        borderRadius: 6,
        border: '1px solid #ddd',
        background: (!companySetup || !order.operationLocation) ? '#eee' : '#4285F4',
        color: (!companySetup || !order.operationLocation) ? '#888' : '#fff',
        cursor: (!companySetup || !order.operationLocation) ? 'not-allowed' : 'pointer'
      }}
    >
      PKW
    </button>
    <button
  type="button"
  onClick={() => openTruckRoute(companySetup, order.operationLocation)}
  disabled={!companySetup || !order.operationLocation}
  style={{
        whiteSpace: 'nowrap',
        padding: '6px 10px',
        borderRadius: 6,
        border: '1px solid #ddd',
        background: (!companySetup || !order.operationLocation) ? '#eee' : '#4285F4',
        color: (!companySetup || !order.operationLocation) ? '#888' : '#fff',
        cursor: (!companySetup || !order.operationLocation) ? 'not-allowed' : 'pointer'
      }}
  title="LKW-Route (GraphHopper) öffnen"
>
  LKW
</button>

  </div>
</label>

            {/* Ansprechpartner vor Ort */}
            <div className="full-width">
              <label htmlFor="lwOperatorId">Ansprechpartner vor Ort:</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <select
                id="lwOperatorId"
                name="lwOperatorId"
                value={order.lwOperatorId}
                onChange={handleLWOperatorChange}
                className="select-box"
                style={{ margin: '10px 0 15px', flex: 1, height: '40px', boxSizing: 'border-box' }}
              >
                <option value="">Bitte Ansprechpartner vor Ort wählen</option>
                {lwOperatorContacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.contactFirstName} {contact.contactLastName}
                  </option>
                ))}
              </select>
                <button
                  type="button"
                  onClick={openNewLWOperatorDialog}
                  disabled={!order.customerId}
                  title={!order.customerId ? 'Bitte zuerst einen Kunden auswählen' : 'Neuen LW-Bediener anlegen'}
                  style={{
                    whiteSpace: 'nowrap',
                    padding: '10px 15px',
                    fontSize: '0.9em',
                    height: '40px',
                    boxSizing: 'border-box',
                    marginTop: '10px',
                    borderRadius: '5px',
                    border: 'none',
                    background: order.customerId ? '#28a745' : '#bbb',
                    color: '#fff',
                    cursor: order.customerId ? 'pointer' : 'not-allowed',
                  }}
                >
                  +
                </button>
                {mode === "edit" && (
                  <button
                    type="button"
                    onClick={() => openEditContactDialog(selectedLWOperator)}
                    disabled={!selectedLWOperator}
                    title={selectedLWOperator
                      ? 'Ausgewählten Ansprechpartner bearbeiten'
                      : 'Bitte zuerst einen Ansprechpartner auswählen'}
                    style={{
                      whiteSpace: 'nowrap',
                      padding: '10px 15px',
                      fontSize: '0.9em',
                      height: '40px',
                      boxSizing: 'border-box',
                      marginTop: '10px',
                      borderRadius: '5px',
                      border: 'none',
                      background: selectedLWOperator ? '#007bff' : '#bbb',
                      color: '#fff',
                      cursor: selectedLWOperator ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Bearbeiten
                  </button>
                )}
              </div>
            </div>

             {/* Checkboxen unterhalb des Bestellers */}
             <div className="checkbox-group" >
                <h3>Als regulärer Miettag zählen:</h3>
                <div className="checkbox-list">
                  {/* Samstag Checkbox */}
                 <label htmlFor="includeSaturday" className="checkbox-item">
          <input
            type="checkbox"
            id="includeSaturday"
            name="includeSaturday"
            checked={tempIncludeSaturday}
            onChange={e =>
              handleGlobalCheckboxChange("includeSaturday", e.target.checked)
            }
            disabled={!isEditing}
          />
          Samstag
        </label>
                

                  {/* Sonntag Checkbox */}
                  <label htmlFor="includeSunday" className="checkbox-item">
          <input
            type="checkbox"
            id="includeSunday"
            name="includeSunday"
            checked={tempIncludeSunday}
            onChange={e =>
              handleGlobalCheckboxChange("includeSunday", e.target.checked)
            }
            disabled={!isEditing}
          />
          Sonntag
        </label>

                  {/* Feiertage Checkbox */}
                   <label htmlFor="includeHolidays" className="checkbox-item">
          <input
            type="checkbox"
            id="includeHolidays"
            name="includeHolidays"
            checked={tempIncludeHolidays}
            onChange={e =>
              handleGlobalCheckboxChange("includeHolidays", e.target.checked)
            }
            disabled={!isEditing}
          />
          Feiertage
        </label>

                </div>

                {/* Neue Checkbox für Reservierung (falls benötigt, sonst entfernen) */}
                {/* Entfernt, da Orders keine Reservierung sind */}
              </div>

            {mode === 'edit' && (
              <label htmlFor="orderNoteTextBlock" className="full-width" style={{ marginTop: '15px' }}>
                Textbaustein für Auftragsnotiz:
                <select
                  id="orderNoteTextBlock"
                  value={selectedOrderNoteTextBlockId}
                  onChange={handleOrderNoteTextBlockChange}
                  disabled={orderNoteTextBlocksLoading || orderNoteTextBlocks.length === 0}
                  style={{ width: '98%', margin: '8px 0 10px 0', padding: '8px', boxSizing: 'border-box' }}
                >
                  <option value="">
                    {orderNoteTextBlocksLoading
                      ? 'Textbausteine werden geladen...'
                      : orderNoteTextBlocks.length === 0
                        ? 'Keine aktiven Textbausteine vorhanden'
                        : 'Textbaustein auswählen...'}
                  </option>
                  {orderNoteTextBlocks.map(textBlock => (
                    <option key={textBlock.id} value={textBlock.id}>
                      {textBlock.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {/* Auftragsnotiz */}
            <label htmlFor="notes" className="full-width" style={{ marginTop: '15px' }}>
              
              <textarea
                id="notes"
                name="notes"
                value={order.notes ?? ""} 
                onChange={handleChange}
                className="textarea-notes"
                rows="5"
                style={{ width: '98%', marginBottom: '20px', marginTop: '20px' }} // Breite kann hier angepasst werden
                placeholder="Auftragsnotiz"
              />
            </label>
            {/* interne Auftragsnotiz */}
            <label htmlFor="internalNotes" className="full-width" style={{ marginTop: '15px' }}>
              
              <textarea
                id="internalNotes"
                name="internalNotes"
                value={order.internalNotes ?? ""} 
                onChange={handleChange}
                className="textarea-notes"
                rows="5"
                style={{ width: '98%', marginBottom: '20px', marginTop: '0px' }} // Breite kann hier angepasst werden
                placeholder="interne Auftragsnotiz"
              />
            </label>
          </div>
          <div className="col"></div>

          {/* Auftragspositionen */}
          <div className="table-breakout" style={{ marginTop: '40px' }}>
            <h3>Auftragspositionen</h3>

            <table className="order-positions-table" >
              <thead>
                <tr>
                  <th style={{ border: '1px solid #ddd', padding: '8px' }}>Position</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px' }}>Artikel</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px' }}>Beschreibung</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px' }}>Einheit</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px' }}>Menge</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', whiteSpace: 'nowrap' }}>E-Preis</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', whiteSpace: 'nowrap' }}>Gesamt</th>
                  <th>Von</th>
                  <th>Bis</th>
                  <th>Zeit Von</th>
                  <th>Zeit Bis</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {[...orderItems]
  .sort((a, b) => (a.designPosition ?? a.position) - (b.designPosition ?? b.position))
  .map((position, index) => {
                  const isRental = position.item?.itemType === 'rental';
                  const isInsuranceItem = position.item?.itemType === 'insurance';
                  const isInsuranceFixedItem = position.item?.itemType === 'insuranceFixed';
                  const editingThisRow = editingRowId === position.id;

                  const unitName =
                    position.item?.machineType?.typePricings?.[0]?.unit?.name ||
                    position.item?.transportPricings?.[0]?.unit?.name ||
                    (isInsuranceItem ? 'Prozent' : 'n.a.');

                  const pricePerUnit = Number(position.pricePerUnit || 0);
                  const total = calcRowTotal(position, orderItems);


                  const isLinkedItem = !!position.mainItemId;

                  // Formatierung des Einzelpreises
                  const formattedPricePerUnit = (typeof pricePerUnit === 'number' && !isNaN(pricePerUnit))
                    ? pricePerUnit.toFixed(2).replace('.', ',') + ' €'
                    : '0,00 €';

                  // Formatierung des Gesamtpreises
                  const formattedTotal = (typeof total === 'number' && !isNaN(total))
                    ? total.toFixed(2).replace('.', ',') + ' €'
                    : '0,00 €';

                  return (
                    <tr key={position.id ?? position.tempId} style={isLinkedItem ? { paddingLeft: '20px' } : {}}>
                      {/* Positionsnummer */}
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{index + 1}</td>

                      {/* Artikelname */}
                      <td style={{ border: '1px solid #ddd', padding: '8px', width: '25ch', minWidth: '25ch', maxWidth: '25ch' }}>
                      {editingThisRow && !isInsuranceItem ? (
                          <input
                            type="text"
                            value={position.item?.name || ''}
                            style={{ width: '25ch', minWidth: '25ch', maxWidth: '25ch' }}
                            onChange={(e) =>
                              setOrderItems((prev) =>
                                prev.map((item) =>
                                  item.id === position.id
                                    ? { ...item, item: { ...item.item, name: e.target.value } }
                                    : item
                                )
                              )
                            }
                          />
                        ) : (
                          <span
                            style={{
                              display: 'inline-block',
                              width: '25ch',
                              minWidth: '25ch',
                              maxWidth: '25ch',
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-all',
                            }}
                          >
                            {position.item?.name}
                          </span>
                        )}
                      </td>

                      {/* Beschreibung */}
<td style={{ border: '1px solid #ddd', padding: '8px' }}>
  {editingThisRow && !isInsuranceItem ? (
    <textarea
      value={position.description ?? position.item?.description ?? ''}
      wrap="soft"
      style={{ maxWidth: '320px' }}
      onChange={(e) =>
        setOrderItems((prev) =>
          prev.map((item) =>
            item.id === position.id
              ? { ...item, description: e.target.value }
              : item
          )
        )
      }
    />
  ) : (
    position.description || position.item?.description || 'Keine Beschreibung verfügbar'
  )}
</td>

                      {/* Einheit */}
                      <td style={{ marginLeft:'0px', border: '1px solid #ddd', padding: '8px' }}>
                      {editingThisRow && !isInsuranceItem && !position.unitLocked ? (
  <select
    value={position.unitId != null ? position.unitId.toString() : ''}
    style={{ minWidth: '150px', marginTop: '0px', marginLeft: '0px' }}
    onChange={(e) => {
      const newUnitId = e.target.value ? parseInt(e.target.value, 10) : null;
      setOrderItems((prev) =>
        prev.map((item) =>
          item.id === position.id
            ? { ...item, unitId: newUnitId }
            
            : item
        )
      );
      updateOrderItemUnit(order.id, position.id, newUnitId, position.quantity);
    }
  }
  >
    <option value="">Einheit wählen</option>
    {position.item?.machineType?.typePricings
      ?.map((pricing) => pricing.unit)
      ?.filter((unit, index, self) => unit && self.findIndex((u) => u.id === unit.id) === index)
      .map((unit) => (
        <option key={unit.id} value={unit.id.toString()}>
          {unit.name}
        </option>
      ))}
  </select>
) : (
  position.unit?.name || ''
)}
                      </td>

                      {/* Menge */}
<td style={{ border: '1px solid #ddd', padding: '8px' }}>
  {editingThisRow && !isInsuranceItem && !isInsuranceFixedItem ? (
    <input
      type="number"
      value={position.quantity || ''}
      min="0"
      onChange={e =>
        setOrderItems(prev =>
          prev.map(item => {
            if (item.id !== position.id) return item;

            const newQty = parseFloat(e.target.value) || 0;

            // ❗ OfferForm-Logik:
            // KEINE Total-Berechnung hier!
            // Total kommt ausschließlich aus calcRowTotal()

            return {
              ...item,
              quantity: newQty,
              manualPriceActive: false
            };
          })
        )
      }
    />
  ) : (
    position.quantity
  )}
</td>




                      {/* Einzelpreis */}
<td style={{ border: '1px solid #ddd', padding: '8px', whiteSpace: 'nowrap' }}>
{editingThisRow && !isInsuranceItem ? (
    <input
      type="number"
      value={position.pricePerUnit || ''}
      style={{ minWidth: '12ch' }}
      onChange={(e) =>
        setOrderItems((prev) =>
          prev.map((item) =>
            item.id === position.id
              ? { 
                  ...item, 
                  pricePerUnit: parseFloat(e.target.value) || 0,
                  manualPriceActive: true  // Flag setzen, wenn der Preis geändert wurde
                }
              : item
          )
        )
      }
      min="0"
      step="0.01"
    />
  ) : (
    // Formatierung des Einzelpreises mit Komma und Euro-Symbol
    (typeof position.pricePerUnit === 'number' && !isNaN(position.pricePerUnit))
      ? position.pricePerUnit.toFixed(2).replace('.', ',') + ' €'
      : '0,00 €'
  )}
</td>

                      {/* Gesamtpreis */}
                      <td style={{ border: '1px solid #ddd', padding: '8px', whiteSpace: 'nowrap' }}>
                          {typeof total === 'number' && !isNaN(total)
                            ? total.toFixed(2).replace('.', ',') + ' €'
                            : '0,00 €'}
                        </td>
                        {isRental && (
                        <>
                           {/* Mietzeitraum Von */}
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                          {editingThisRow && !isInsuranceItem ? (
                              <input
                                type="date"
                                value={position.rentalPeriodFrom || ''}
                                onChange={(e) =>
                                  setOrderItems((prev) =>
                                    prev.map((item) =>
                                      item.id === position.id
                                        ? { ...item, rentalPeriodFrom: e.target.value }
                                        : item
                                    )
                                  )
                                }
                              />
                            ) : (
                              position.rentalPeriodFrom ? formatDate(position.rentalPeriodFrom) : '-'
                            )}
                          </td>

                           {/* Mietzeitraum Bis */}
<td style={{ border: '1px solid #ddd', padding: '8px' }}>
{editingThisRow && !isInsuranceItem ? (
    <input
      type="date"
      value={position.rentalPeriodTo || ''}
      readOnly // Verhindert Benutzereingaben
      style={{
        backgroundColor: '#f5f5f5', // Optional: Zeigt visuell an, dass das Feld nicht bearbeitet werden kann
        cursor: 'not-allowed', // Zeigt einen visuellen Hinweis an, dass das Feld gesperrt ist
      }}
    />
  ) : (
    position.rentalPeriodTo ? formatDate(position.rentalPeriodTo) : '-'
  )}
</td>

                          {/* Mietzeit Von */}
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                          {editingThisRow && !isInsuranceItem ? (
                              <input
                                type="time"
                                value={position.rentalTimeFrom || ''}
                                onChange={(e) => {
                                  console.log('Änderung in rentalTimeFrom:', e.target.value); // Debugging
                                  setOrderItems((prev) =>
                                    prev.map((item) =>
                                      item.id === position.id
                                        ? { ...item, rentalTimeFrom: e.target.value }
                                        : item
                                    )
                                  );
                                }}
                              />
                            ) : (
                              position.rentalTimeFrom || '-'
                            )}
                          </td>

                          {/* Mietzeit Bis */}
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                          {editingThisRow && !isInsuranceItem ? (
                              <input
                                type="time"
                                value={position.rentalTimeTo || ''}
                                onChange={(e) =>
                                  setOrderItems((prev) =>
                                    prev.map((item) =>
                                      item.id === position.id
                                        ? { ...item, rentalTimeTo: e.target.value }
                                        : item
                                    )
                                  )
                                }
                              />
                            ) : (
                              position.rentalTimeTo || '-'
                            )}
                          </td>
                        </>
                      )}

                      {!isRental && (
                        <td colSpan={4} style={{ border: '1px solid #ddd', padding: '8px' }}></td>
                      )}

                     {/* Aktionen */}
                     <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                     {editingThisRow ? (
                          <div style={{ display: 'flex' }}>
                            <button
                              type="button"
                              onClick={() => saveEditedRowOrder(position.id)}
                              style={{
                                fontSize: '15px',
                                padding: '5px 10px',
                                background: '#28a745',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '5px',
                              }}
                            >
                              Speichern
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelEdit}
                              style={{
                                fontSize: '15px',
                                padding: '5px 10px',
                                background: '#6c757d',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '5px',
                                marginLeft: '10px',
                              }}
                            >
                              Abbrechen
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex' }}>
                            {/* Verstecke/Deaktiviere den Bearbeiten-Button, falls insurance und insuranceFixed*/}
            { !isInsuranceItem
   && position.item?.itemType !== 'insuranceFixed' && (
                            <button
                type="button"
                onClick={() => handleEditClick(position.id)}
                style={{
                  fontSize: '15px',
                  padding: '5px 10px',
                  background: '#007bff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '5px',
                  marginRight: '10px',
                }}
              >
                Bearbeiten
              </button>
              )}
                            <button
                              type="button"
                              onClick={() => removeOrderItem(position.id)}
                              style={{
                                fontSize: '15px',
                                padding: '5px 10px',
                                background: '#dc3545',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '5px',
                              }}
                            >
                              Löschen
                            </button>
<div style={{ display: 'inline-flex', gap: '4px', marginLeft: '6px' }}>
  {/* ...deine bisherigen Buttons (Bearbeiten, Löschen) */}
  <button
    type="button"
    title="Nach oben"
    onClick={() => moveOrderItemUp(position.id)}
    disabled={index === 0}
    style={{ marginRight: 6, padding: '2px 6px', color: '#fff', background: '#aaaaaa',}}
  >▲</button>
  <button
    type="button"
    title="Nach unten"
    onClick={() => moveOrderItemDown(position.id)}
    disabled={index === orderItems.length - 1}
    style={{ padding: '2px 6px' , color: '#fff', background: '#aaaaaa',}}
  >▼</button>
</div>

                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
             </div>

            {/* Gesamtsummen-Tabelle */}
            <div style={{ marginTop: '20px', width: '100%', maxWidth: '800px' }}>
              <h3>Zusammenfassung</h3>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <tbody>
                  {/* Gesamt Netto */}
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'normal' }}>Gesamt Netto</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                      {/* Berechnung von totalNet */}
                      {totalNetCalc.toFixed(2).replace('.', ',') + ' €'}
                    </td>
                  </tr>

                  {/* MwSt. */}
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'normal' }}>zzgl. MwSt. ({order.vatRate ?? 19}%)</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                      {/* Berechnung der MwSt. */}
                      {vatAmountCalc.toFixed(2).replace('.', ',') + ' €'}

                    </td>
                  </tr>

                  {/* Brutto */}
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }}>Rechnungsbetrag</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                      {/* Berechnung des Bruttobetrags */}
                      {totalGrossCalc.toFixed(2).replace('.', ',') + ' €'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
         

          {/* Neue Auftragsposition hinzufügen */}
          <div className="order-item-addition" style={{ marginTop: '20px' }}>
            <h3>Neue Auftragsposition hinzufügen</h3>
            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label htmlFor="quantity">Menge:</label>
              <input
                type="number"
                id="quantity"
                value={selectedQuantity}
                onChange={(e) => setSelectedQuantity(Number(e.target.value || 0))}
                min="1"
                style={{ display: 'block', marginTop: '5px', marginBottom: '10px',  padding: '8px', width: '100%', marginLeft: '0px'}}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label htmlFor="itemId"></label>
              <Select
                id="itemId"
                options={itemOptions}
                value={
                  items
                    .map((item) => ({ value: item.id, label: formatItemOptionLabel(item) }))
                    .find((option) => option.value === selectedItemId) || null
                } // Aktuell ausgewählt
                onChange={(selectedOption) => setSelectedItemId(selectedOption ? selectedOption.value : null)} // Auswahl speichern
                onInputChange={(inputValue, meta) => {
  if (meta.action === "input-change") {
    fetchFilteredItems(inputValue);
  }
  return inputValue;
}}

                placeholder="Artikel suchen..."
                isClearable
                filterOption={safeFilterOption}
                styles={{
                  control: (base) => ({
                    ...base,
                    marginTop: '10px',
                    width: '100%',
                  
                  }),
                }}
              />
            </div>
            
            <button type="button" onClick={addOrderItem} style={{ padding: '10px 20px', cursor: 'pointer' }}>
              Auftragsposition hinzufügen
            </button>
          </div>
        </div>

        {/* Rechte Spalte - Auftragsnummer und Besteller */}
        <div className="col">

          <div style={{ marginBottom: '20px' }}>
  <label htmlFor="workshopOrderDropdown" style={{ marginRight: '10px', fontWeight: 'bold' }}>
    
  </label>
  <select
    id="workshopOrderDropdown"
    onChange={(e) => {
      const selectedWorkshopOrderId = e.target.value;
      if (selectedWorkshopOrderId) {
        navigate(`/workshoporders/${selectedWorkshopOrderId}/edit`);
      }
    }}
    style={{
      padding: '10px',
      borderRadius: '4px',
      border: '1px solid #ccc',
      cursor: 'pointer',
      width: '250px',
    }}
  >
    <option value="">Werkstattauftrag wählen</option>
    {workshopOrders.map((wo) => (
      <option key={wo.id} value={wo.id}>
        {`${wo.workshopOrderNumber} (${wo.workshopOrderType})`}
      </option>
    ))}
  </select>
</div>

          

           {/* Dropdown-Feld für Rechnungen */}
{order.invoices && order.invoices.filter(inv => !inv.creditNotes || inv.creditNotes.length === 0).length > 0 && (
  <div style={{ marginBottom: '20px' }}>
    <label htmlFor="invoiceDropdown" style={{ marginRight: '10px', fontWeight: 'bold' }}>
      
    </label>
    <select
      id="invoiceDropdown"
      onChange={(e) => {
        const selectedInvoiceId = e.target.value;
        if (selectedInvoiceId) {
          navigate(`/invoices/${selectedInvoiceId}/edit`);
        }
      }}
      style={{
        padding: '10px',
        borderRadius: '4px',
        border: '1px solid #ccc',
        cursor: 'pointer',
        width: '250px',
      }}
    >
      <option value="">Rechnung wählen</option>
      {order.invoices
        .filter(inv => !inv.creditNotes || inv.creditNotes.length === 0)
        .map((invoice) => (
          <option key={invoice.id} value={invoice.id}>
            {`${invoice.invoiceNumber || invoice.id} (${invoice.totalNet} €)`}
          </option>
        ))}
    </select>
    
  </div>
  
)}


          
          {/* Auftragsnummer, Auftragsdatum und Auftragsgültigkeit anzeigen*/}
          {order.orderNumber && (
            <div>
              <p><strong>Auftragsnummer:</strong> {order.orderNumber}</p>
              <p><strong>Auftragsdatum:</strong> {order.date ? formatDate(order.date) : formatDate(new Date())}</p>
              {/* Neues Eingabefeld für referenceNumber */}
    <label htmlFor="referenceNumber" className="full-width">
      <strong></strong>
      <input
        type="text"
        id="referenceNumber"
        name="referenceNumber"
        placeholder="Bestellnummer"
        value={order.referenceNumber || ""}
        style={{
          padding: '10px',
          borderRadius: '4px',
          border: '1px solid #ccc',
          
          width: '250px',
        }}
        onChange={handleChange}
        className="input-box"
      />
    </label>
              
            </div>
          )}

          <div className="full-width">
            <label htmlFor="contactId">Besteller:</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', justifyContent: 'center', flexWrap: 'wrap' }}>
              <select
                id="contactId"
                name="contactId"
                value={order.contactId}
                onChange={handleContactChange}
                className="select-box-bst"
                style={{
                  flex: '0 1 304px', minWidth: 0, maxWidth: '80%',
                  margin: '10px 0 15px'
                }}
              >
                <option value="">Besteller wählen</option>
                {customerContacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.contactFirstName} {contact.contactLastName}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={openNewBestellerDialog}
                disabled={!order.customerId}
                title={!order.customerId ? 'Bitte zuerst einen Kunden auswählen' : 'Neuen Besteller anlegen'}
                style={{
                  whiteSpace: 'nowrap', padding: '10px 15px', fontSize: '0.9em',
                  borderRadius: '5px', border: 'none', marginTop: '10px',
                  background: order.customerId ? '#28a745' : '#bbb', color: '#fff',
                  cursor: order.customerId ? 'pointer' : 'not-allowed',
                }}
              >
                + 
              </button>
              {mode === "edit" && (
                <button
                  type="button"
                  onClick={() => openEditContactDialog(selectedContact)}
                  disabled={!selectedContact}
                  title={selectedContact
                    ? 'Ausgewählten Besteller bearbeiten'
                    : 'Bitte zuerst einen Besteller auswählen'}
                  style={{
                    whiteSpace: 'nowrap', padding: '10px 15px', fontSize: '0.9em',
                    borderRadius: '5px', border: 'none', marginTop: '10px',
                    background: selectedContact ? '#007bff' : '#bbb', color: '#fff',
                    cursor: selectedContact ? 'pointer' : 'not-allowed',
                  }}
                >
                  Bearbeiten
                </button>
              )}
            </div>
            {customerContacts.length === 0 && (
              <small style={{ color: '#666' }}>Für diesen Kunden ist noch kein Besteller hinterlegt.</small>
            )}
          </div>

          {selectedContact && (
            <div className="contact-details">
              <p><strong>Name:</strong> {selectedContact.contactFirstName} {selectedContact.contactLastName}</p>
              <p><strong>Mobil:</strong> {selectedContact.contactMobile}</p>
              <p><strong>Email:</strong> {selectedContact.contactEmail}</p>
            </div>
          )}
          
          <div style={{ textAlign: 'center' }}>
  <button
    type="button"
    onClick={() => setShowWorkshopOrderDialog(true)}
    style={{
      marginTop: '10px',
      padding: '10px',
      fontSize: '0.9em',
      backgroundColor: '#28a745',
      color: '#fff',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
      width: '250px',
    }}
  >
    Werkstattauftrag erstellen
  </button>
  </div>
  {/* ⬇️ NEU: Checkbox direkt darunter platzieren */}
  <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
    <label style={{ display: 'inline-flex', alignItems: 'center',  width: 150 }}>
      <input
        type="checkbox"
        checked={!!order.includeLaddersInEmail}
        onChange={(e) =>
          setOrder((prev) => ({ ...prev, includeLaddersInEmail: e.target.checked }))
        }
        style={{ marginRight: 0 }}
      />
      Preisstaffeln in E-Mail
    </label>
  </div>
        </div>
        
      </div>

      <div className="actions">
      {/*  <button type="button" onClick={handleBackButtonClick} style={{ marginRight: '10px', padding: '10px 20px', cursor: 'pointer' }}>
          Zurück
        </button>*/}
        <button type="button" onClick={handleSave} style={{ padding: '10px 20px', cursor: 'pointer' }}>
          {mode === "edit" ? "Speichern" : "Erstellen"}
        </button>
      </div>

      {/* Hier wird der modale Dialog eingefügt */}
    {showNewBestellerDialog && (
      <div
        role="presentation"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) closeNewBestellerDialog();
        }}
        style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1100, padding: 16,
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-besteller-title"
          style={{
            background: '#fff', padding: 20, borderRadius: 8, width: '100%',
            maxWidth: 480, boxShadow: '0 8px 28px rgba(0, 0, 0, 0.25)',
          }}
        >
          <h3 id="new-besteller-title" style={{ marginTop: 0 }}>Besteller anlegen</h3>
          <p style={{ marginTop: 0, color: '#555' }}>
            Der Kontakt wird als Besteller bei {selectedCustomer?.company || 'diesem Kunden'} gespeichert.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>
              Vorname*
              <input
                type="text" name="contactFirstName" value={newBesteller.contactFirstName}
                onChange={handleNewBestellerChange} autoFocus
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </label>
            <label>
              Nachname*
              <input
                type="text" name="contactLastName" value={newBesteller.contactLastName}
                onChange={handleNewBestellerChange}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </label>
            <label>
              Telefon
              <input
                type="tel" name="contactPhone" value={newBesteller.contactPhone}
                onChange={handleNewBestellerChange}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </label>
            <label>
              Mobil
              <input
                type="tel" name="contactMobile" value={newBesteller.contactMobile}
                onChange={handleNewBestellerChange}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </label>
            <label style={{ gridColumn: '1 / -1' }}>
              E-Mail
              <input
                type="email" name="contactEmail" value={newBesteller.contactEmail}
                onChange={handleNewBestellerChange}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </label>
          </div>

          {newBestellerError && (
            <div role="alert" style={{ color: '#b42318', marginTop: 12 }}>
              {newBestellerError}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <button
              type="button" onClick={closeNewBestellerDialog} disabled={isSavingBesteller}
              style={{
                padding: '8px 12px', backgroundColor: '#6c757d', color: '#fff', border: 'none',
                borderRadius: 5, cursor: isSavingBesteller ? 'not-allowed' : 'pointer'
              }}
            >
              Abbrechen
            </button>
            <button
              type="button" onClick={saveNewBesteller} disabled={isSavingBesteller}
              style={{
                padding: '8px 12px', backgroundColor: '#28a745', color: '#fff', border: 'none',
                borderRadius: 5, cursor: isSavingBesteller ? 'wait' : 'pointer'
              }}
            >
              {isSavingBesteller ? 'Wird gespeichert …' : 'Anlegen und auswählen'}
            </button>
          </div>
        </div>
      </div>
    )}

    {showNewLWOperatorDialog && (
      <div
        role="presentation"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) closeNewLWOperatorDialog();
        }}
        style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1100, padding: 16,
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-lw-operator-title"
          style={{
            background: '#fff', padding: 20, borderRadius: 8, width: '100%',
            maxWidth: 480, boxShadow: '0 8px 28px rgba(0, 0, 0, 0.25)',
          }}
        >
          <h3 id="new-lw-operator-title" style={{ marginTop: 0 }}>
            Ansprechpartner vor Ort anlegen
          </h3>
          <p style={{ marginTop: 0, color: '#555' }}>
            Der Kontakt wird als LW-Bediener bei {selectedCustomer?.company || 'diesem Kunden'} gespeichert.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>
              Vorname*
              <input
                type="text"
                name="contactFirstName"
                value={newLWOperator.contactFirstName}
                onChange={handleNewLWOperatorChange}
                autoFocus
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </label>
            <label>
              Nachname*
              <input
                type="text"
                name="contactLastName"
                value={newLWOperator.contactLastName}
                onChange={handleNewLWOperatorChange}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </label>
            <label>
              Telefon
              <input
                type="tel"
                name="contactPhone"
                value={newLWOperator.contactPhone}
                onChange={handleNewLWOperatorChange}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </label>
            <label>
              Mobil
              <input
                type="tel"
                name="contactMobile"
                value={newLWOperator.contactMobile}
                onChange={handleNewLWOperatorChange}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </label>
            <label style={{ gridColumn: '1 / -1' }}>
              E-Mail
              <input
                type="email"
                name="contactEmail"
                value={newLWOperator.contactEmail}
                onChange={handleNewLWOperatorChange}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </label>
          </div>

          {newLWOperatorError && (
            <div role="alert" style={{ color: '#b42318', marginTop: 12 }}>
              {newLWOperatorError}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <button
              type="button"
              onClick={closeNewLWOperatorDialog}
              disabled={isSavingLWOperator}
              style={{
                padding: '8px 12px', backgroundColor: '#6c757d', color: '#fff',
                border: 'none', borderRadius: 5, cursor: isSavingLWOperator ? 'not-allowed' : 'pointer'
              }}
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={saveNewLWOperator}
              disabled={isSavingLWOperator}
              style={{
                padding: '8px 12px', backgroundColor: '#28a745', color: '#fff',
                border: 'none', borderRadius: 5, cursor: isSavingLWOperator ? 'wait' : 'pointer'
              }}
            >
              {isSavingLWOperator ? 'Wird gespeichert …' : 'Anlegen und auswählen'}
            </button>
          </div>
        </div>
      </div>
    )}

    {contactToEdit && (
      <div
        role="presentation"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) closeEditContactDialog();
        }}
        style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1100, padding: 16,
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-order-contact-title"
          style={{
            background: '#fff', padding: 20, borderRadius: 8, width: '100%',
            maxWidth: 480, boxShadow: '0 8px 28px rgba(0, 0, 0, 0.25)',
          }}
        >
          <h3 id="edit-order-contact-title" style={{ marginTop: 0 }}>
            {String(contactToEdit.contactFunction || '').toLowerCase() === 'besteller'
              ? 'Besteller bearbeiten'
              : 'Ansprechpartner vor Ort bearbeiten'}
          </h3>
          <p style={{ marginTop: 0, color: '#555' }}>
            Die Änderung wird zentral beim Kontakt von {selectedCustomer?.company || 'diesem Kunden'}
            {' '}gespeichert und ist dadurch auch in anderen Vorgängen sichtbar.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>
              Vorname*
              <input
                type="text"
                name="contactFirstName"
                value={contactToEdit.contactFirstName}
                onChange={handleEditContactChange}
                autoFocus
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </label>
            <label>
              Nachname*
              <input
                type="text"
                name="contactLastName"
                value={contactToEdit.contactLastName}
                onChange={handleEditContactChange}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </label>
            <label>
              Telefon
              <input
                type="tel"
                name="contactPhone"
                value={contactToEdit.contactPhone}
                onChange={handleEditContactChange}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </label>
            <label>
              Mobil
              <input
                type="tel"
                name="contactMobile"
                value={contactToEdit.contactMobile}
                onChange={handleEditContactChange}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </label>
            <label style={{ gridColumn: '1 / -1' }}>
              E-Mail
              <input
                type="email"
                name="contactEmail"
                value={contactToEdit.contactEmail}
                onChange={handleEditContactChange}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </label>
          </div>

          {editContactError && (
            <div role="alert" style={{ color: '#b42318', marginTop: 12 }}>
              {editContactError}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <button
              type="button"
              onClick={closeEditContactDialog}
              disabled={isSavingEditedContact}
              style={{
                padding: '8px 12px', backgroundColor: '#6c757d', color: '#fff',
                border: 'none', borderRadius: 5,
                cursor: isSavingEditedContact ? 'not-allowed' : 'pointer'
              }}
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={saveEditedContact}
              disabled={isSavingEditedContact}
              style={{
                padding: '8px 12px', backgroundColor: '#007bff', color: '#fff',
                border: 'none', borderRadius: 5,
                cursor: isSavingEditedContact ? 'wait' : 'pointer'
              }}
            >
              {isSavingEditedContact ? 'Wird gespeichert …' : 'Änderungen speichern'}
            </button>
          </div>
        </div>
      </div>
    )}

    {showWorkshopOrderDialog && (
      <div
        className="modal-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
      >
        <div
          className="modal-content"
          style={{
            background: '#fff',
            padding: '20px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '400px',
          }}
        >
          <h3>Werkstattauftrag erstellen</h3>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ marginBottom: '5px', display: 'block' }}>
              Rental-Artikel auswählen:
            </label>
            <Select
              options={rentalOptions}
              value={selectedRentalArticle}
              onChange={(option) => setSelectedRentalArticle(option)}
              placeholder="Rental-Artikel wählen..."
              isClearable
            />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ marginBottom: '5px', display: 'block' }}>
              Auftragstyp auswählen:
            </label>
            <Select
              options={workshopOrderTypeOptions}
              value={selectedWorkshopOrderType}
              onChange={(option) => setSelectedWorkshopOrderType(option)}
              placeholder="Auftragstyp wählen..."
              isClearable={false}
              styles={{
                control: (base) => ({
                  ...base,
                  marginTop: '10px',
                  width: '100%',
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 11,
                }),
              }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              type="button"
              onClick={() => setShowWorkshopOrderDialog(false)}
              style={{
                padding: '8px 12px',
                backgroundColor: '#6c757d',
                color: '#fff',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={handleConfirmWorkshopOrderCreation}
              style={{
                padding: '8px 12px',
                backgroundColor: '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              Erstellen
            </button>
            </div>
        </div>
      </div>
    )}
        





    </div>
  );
};

export default OrderForm;
